import { Disclosure, Menu } from '@headlessui/react';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Loader2,
  Mail,
  MapPin,
  MoreHorizontal,
  Phone,
  Plus,
  Printer,
  RefreshCw,
  Send,
  Star,
  Trash2,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { MakeCallModal } from '@/components/common/calls/make-call-modal/MakeCallModal';
import { formatPhoneNumberForDisplay } from '@/components/correspondence/transmission/validation';
import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { SendFrontEmailDialog } from '@/components/matter/SendFrontEmailDialog';
import { AddProviderModal } from '@/components/provider-directory/AddProviderModal';
import { DocumentSelectionModal } from '@/components/tasks/task-detail/DocumentSelectionModal';
import { EnrichmentRecordsGuidance } from '@/components/treatment/enrichment';
import { SelectDestinationModal } from '@/components/treatment/v3/request/modals/SelectDestinationModal';
import { ResendRequestModal } from '@/components/treatment/v3/request/resend/ResendRequestModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge, BadgeButton, BadgeColor } from '@/components/ui/catalyst/badge';
import { Button as CatalystButton } from '@/components/ui/catalyst/button';
import {
  Dialog as CatalystDialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle as CatalystDialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ColorVariant, Dropdown } from '@/components/ui/v3/Dropdown';
import { Tooltip } from '@/components/ui/v3/Tooltip';
import {
  Z_INDEX_DROPDOWN_IN_MODAL,
  Z_INDEX_MODAL_BASE,
  Z_INDEX_MODAL_NESTED,
} from '@/components/ui/zIndex';
import { useMatter } from '@/hooks/useMatter';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import { useRequestFaxStatus } from '@/hooks/useRequestFaxStatus';
import { hasBlockingMissingFields } from '@/lib/missingFields';
import { toTitleCase } from '@/lib/strings';
import { useRefreshFaxStatusMutation } from '@/services/api/faxService';
import { firmsApiClient } from '@/services/api/firmsService';
import {
  SubmitHipaaPackageSchema,
  TreatmentRecordRequestSchema,
  TreatmentRecordRequestStatus,
  TreatmentRecordTypes,
  UpdateTreatmentRecordRequestChannelSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import {
  hipaaPackageApi,
  useGetPackageQuery,
} from '@/services/api/hipaaPackageService';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import { mattersApiClient } from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { useUpdateMedicalProviderMutation } from '@/services/api/treatmentService';
import {
  ContactHandle,
  ContactHandleType,
  MedicalProvider,
  ProcedureType,
  ProviderStatus,
  ProviderTreatmentStatus,
  TreatmentCareLevel,
} from '@/services/types/treatment-types';
import {
  getExtendedMetadata,
  getHipaaSentDate,
} from '@/types/treatment-record-request-extended';
import type { AlternateContact } from '@/types/treatment-request-alternates';
import { getEnrichmentFromMentions } from '@/utils/enrichment-adapter';
import { canonicalizeHandleType } from '@/utils/treatment-request-alternates';

import { IdentityStatusBadge } from '../../medical-provider/IdentityStatusBadge';
import { StatusOptions } from '../constants';
import { useAlternateContacts } from '../request/hooks/useAlternateContacts';
import { useRequestResendActions } from '../request/hooks/useRequestResendActions';
import { CreateProviderRequestDialog } from '../request/modals/CreateRecordRequestModal';
import { getRequestOrigin } from '../request/plan-utils';
import { RequestMethodSelector } from '../request/request-details/RequestMethodSelector';
import {
  BlockedState,
  ResolveBlockersButton,
} from '../request/tabs/records/ResolveBlockersButton';
import {
  formatProviderTreatmentDates,
  getProviderTreatmentDates,
} from '../utils/index';
import { CompactFollowUps } from './CompactFollowUps';
import { FollowUpBadge } from './FollowUpBadge';
import { ManageProvidersModal } from './ManageProvidersModal';
import {
  getNextRequestProgressIndex,
  getRequestProgressIndex as getStepIndex,
  RECORD_REQUEST_PROGRESS_STEPS as PROGRESS_STEPS,
} from './recordRequestProgress';

// Status to label/color mapping
// Front channel ID for records@finchlegal.com (shared fallback sender)
const RECORDS_FRONT_CHANNEL_ID = 'cha_j9s4e';
const RECORDS_SENDER_ADDRESS = 'records@finchlegal.com';

const StatusToLabel: Record<
  TreatmentRecordRequestStatus,
  { label: string; color: BadgeColor }
> = {
  not_ready: { label: 'Not Ready', color: 'zinc' },
  pending: { label: 'Send to Provider', color: 'yellow' },
  request_submitted: { label: 'Confirm Receipt', color: 'blue' },
  request_received: { label: 'Follow-Up on Processing', color: 'sky' },
  awaiting_invoice: { label: 'Await Invoice', color: 'orange' },
  pending_payment: { label: 'Pay the Invoice', color: 'amber' },
  records_pending: { label: 'Confirm Payment Receipt', color: 'violet' },
  records_delivered: { label: 'Wait for Records', color: 'green' },
  records_ingested: { label: 'Completed', color: 'emerald' },
  rejected: { label: 'Rejected', color: 'red' },
  cancelled: { label: 'Cancelled', color: 'rose' },
  auto_retried: { label: 'Auto-Retried', color: 'zinc' },
};

const ChannelTypeLabels: Record<string, string> = {
  portal: 'Portal',
  human: 'Human',
  email: 'Email',
  fax: 'Fax',
  mail: 'Mail',
  phone: 'Phone',
};

const procedureTypeLabel: Partial<Record<ProcedureType, string>> = {
  [ProcedureType.ER_VISIT]: 'ER',
  [ProcedureType.URGENT_CARE]: 'Urgent Care',
  [ProcedureType.CHIROPRACTIC_VISIT]: 'Chiro',
  [ProcedureType.PT_VISIT]: 'PT',
  [ProcedureType.MRI]: 'MRI',
  [ProcedureType.CT_SCAN]: 'CT',
  [ProcedureType.XRAY]: 'X-Ray',
  [ProcedureType.EMG_NCV]: 'EMG/NCV',
  [ProcedureType.PAIN_MANAGEMENT_CONSULT]: 'Pain Mgmt',
  [ProcedureType.EPIDURAL_INJECTION]: 'Epidural',
  [ProcedureType.NERVE_BLOCK]: 'Nerve Block',
  [ProcedureType.SURGERY_RECOMMENDED]: 'Surgery Rec.',
  [ProcedureType.SURGERY_PERFORMED]: 'Surgery',
  [ProcedureType.ORTHO_CONSULT]: 'Ortho',
  [ProcedureType.OTHER]: 'Other',
};

// Animated dashed line (inspired by LOA Chaser)
const recordRequestProgressAnimatedStyles = `
  @keyframes dash-move {
    0% { background-position: 0 0; }
    100% { background-position: 12px 0; }
  }
`;

// Helper to check if a request has Voice AI contact data
const hasVoiceAIContactData = (metadata: unknown) => {
  const extended = getExtendedMetadata(metadata);
  if (!extended) return false;
  const summary = extended.contact_discovery_summary;
  return Boolean(
    summary?.call_summary?.trim() ||
      summary?.submission_methods?.length ||
      summary?.alternate_contacts?.length ||
      extended.voice_ai_call_summary?.trim() ||
      extended.voice_ai_discovered_alternates?.length,
  );
};

// Helper to format status labels
const formatStatusLabel = (status?: string | null) => {
  if (!status) return 'Unknown';
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// Helper to get handle icon based on type
const getHandleIcon = (type: string) => {
  const canonical = canonicalizeHandleType(type);
  switch (canonical) {
    case 'FAX':
      return <Printer className="h-3 w-3 text-zinc-500" />;
    case 'PHONE':
      return <Phone className="h-3 w-3 text-zinc-500" />;
    case 'EMAIL':
      return <Mail className="h-3 w-3 text-zinc-500" />;
    default:
      return <MapPin className="h-3 w-3 text-zinc-500" />;
  }
};

// Compact Voice AI Info - shows Voice AI call summary and discovered info
interface CompactVoiceAIInfoProps {
  metadata: ReturnType<typeof getExtendedMetadata>;
}

const CompactVoiceAIInfo = ({ metadata }: CompactVoiceAIInfoProps) => {
  const [isFullTextOpen, setIsFullTextOpen] = useState(false);

  if (!metadata) return null;

  const summary = metadata.contact_discovery_summary;
  const callSummary =
    summary?.call_summary?.trim() || metadata.voice_ai_call_summary?.trim();

  if (!callSummary) return null;

  // Show "Read more" if text is longer than ~200 chars
  const isLongText = callSummary.length > 200;

  return (
    <>
      <Disclosure>
        {({ open }) => (
          <div className="border-t border-zinc-100 pt-2 mt-2">
            <Disclosure.Button className="flex w-full items-center justify-between text-xs py-1">
              <div className="flex items-center gap-1.5">
                <Phone className="h-3 w-3 text-blue-600" />
                <span className="font-medium text-blue-700">
                  Voice AI Results
                </span>
              </div>
              <ChevronDown
                className={`h-3 w-3 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="mt-2 space-y-2">
              <p className="text-xs text-zinc-700 line-clamp-4 whitespace-pre-line">
                {callSummary}
              </p>
              {isLongText && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFullTextOpen(true);
                  }}
                  className="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                >
                  Read full summary...
                </button>
              )}
              {summary?.required_documents &&
                summary.required_documents.length > 0 && (
                  <div className="text-[10px] text-zinc-500">
                    <span className="font-medium">Required:</span>{' '}
                    {summary.required_documents.join(', ')}
                  </div>
                )}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>

      {/* Full Text Dialog */}
      <Dialog open={isFullTextOpen} onOpenChange={setIsFullTextOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-blue-600" />
              Voice AI Call Summary
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed">
              {callSummary}
            </p>
            {summary?.submission_methods &&
              summary.submission_methods.length > 0 && (
                <div className="border-t pt-3">
                  <h4 className="text-xs font-medium text-zinc-900 mb-2">
                    Discovered Submission Methods
                  </h4>
                  <div className="space-y-2">
                    {summary.submission_methods.map((method, i) => {
                      if (typeof method === 'string') {
                        // Parse string format like "Fax: (555) 123-4567 - Description (95% confidence)"
                        const parts = method.split(' - ');
                        const mainPart = parts[0];
                        const description = parts.slice(1).join(' - ');

                        return (
                          <div
                            key={i}
                            className="flex items-start gap-2 py-1.5 px-2 bg-zinc-50 rounded"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-zinc-800">
                                {mainPart}
                              </p>
                              {description && (
                                <p className="text-xs text-zinc-500 mt-0.5">
                                  {description}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      }
                      // Handle object format
                      const methodObj = method as Record<string, unknown>;
                      const methodType = String(
                        methodObj.method_type ||
                          methodObj.submission_method ||
                          'Unknown',
                      );
                      const methodValue = String(methodObj.value || '');
                      const methodNotes = methodObj.notes
                        ? String(methodObj.notes)
                        : null;
                      const methodConfidence =
                        typeof methodObj.confidence === 'number'
                          ? methodObj.confidence
                          : null;

                      return (
                        <div
                          key={i}
                          className="flex items-start gap-2 py-1.5 px-2 bg-zinc-50 rounded"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-zinc-800">
                              {methodType}: {methodValue}
                            </p>
                            {methodNotes && (
                              <p className="text-xs text-zinc-500 mt-0.5">
                                {methodNotes}
                              </p>
                            )}
                            {methodConfidence !== null && (
                              <span className="text-xs text-zinc-400">
                                {Math.round(methodConfidence * 100)}% confidence
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            {summary?.required_documents &&
              summary.required_documents.length > 0 && (
                <div className="border-t pt-3">
                  <h4 className="text-xs font-medium text-zinc-900 mb-2">
                    Required Documents
                  </h4>
                  <ul className="list-disc list-inside space-y-1">
                    {summary.required_documents.map((doc, i) => (
                      <li key={i} className="text-xs text-zinc-600">
                        {doc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Compact Transmission Status - shows fax/email delivery status inline
interface CompactTransmissionStatusProps {
  channelType?: string | null;
  faxStatus?: {
    status?: string | null;
    delivered_at?: string | null;
    sent_at?: string | null;
    failed_at?: string | null;
    error_message?: string | null;
  } | null;
  emailStatus?: {
    status?: string | null;
    conversation_status?: string | null;
    sent_at?: string | null;
    front_conversation_url?: string | null;
  } | null;
  isEmailLoading?: boolean;
  onRefreshFax?: () => void;
  isFaxRefreshing?: boolean;
}

const CompactTransmissionStatus = ({
  channelType,
  faxStatus,
  emailStatus,
  isEmailLoading,
  onRefreshFax,
  isFaxRefreshing,
}: CompactTransmissionStatusProps) => {
  // Only show for fax/email channels
  if (channelType !== 'fax' && channelType !== 'email') {
    return null;
  }

  // Fax status
  if (channelType === 'fax') {
    if (!faxStatus?.status) return null;

    const statusColors: Record<string, string> = {
      delivered: 'text-green-600',
      sent: 'text-blue-600',
      pending: 'text-yellow-600',
      sending: 'text-yellow-600',
      failed: 'text-red-600',
    };
    const statusColor = statusColors[faxStatus.status] || 'text-zinc-600';
    const isTerminalStatus =
      faxStatus.status === 'delivered' || faxStatus.status === 'failed';

    return (
      <div className="flex items-center gap-1.5 text-xs border-t border-zinc-100 pt-2 mt-2">
        <Printer className="h-3 w-3 text-zinc-400" />
        <span className={statusColor}>
          Fax {formatStatusLabel(faxStatus.status)}
        </span>
        {faxStatus.error_message && (
          <Tooltip content={faxStatus.error_message}>
            <ExclamationTriangleIcon className="h-3 w-3 text-red-500 cursor-help" />
          </Tooltip>
        )}
        {onRefreshFax && (
          <Tooltip
            content={
              isTerminalStatus ? 'Refresh status' : 'Checking for delivery...'
            }
          >
            <button
              type="button"
              onClick={onRefreshFax}
              disabled={isFaxRefreshing}
              className="p-0.5 text-zinc-400 hover:text-zinc-600 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-3 w-3 ${isFaxRefreshing ? 'animate-spin' : ''}`}
              />
            </button>
          </Tooltip>
        )}
      </div>
    );
  }

  // Email status
  if (channelType === 'email') {
    if (isEmailLoading) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-zinc-500 border-t border-zinc-100 pt-2 mt-2">
          <Mail className="h-3 w-3" />
          <span>Checking email status...</span>
        </div>
      );
    }

    if (emailStatus) {
      const status =
        emailStatus.conversation_status ||
        (emailStatus.sent_at ? 'sent' : null) ||
        emailStatus.status;
      if (!status) return null;

      return (
        <div className="flex items-center gap-1.5 text-xs border-t border-zinc-100 pt-2 mt-2">
          <Mail className="h-3 w-3 text-zinc-400" />
          <span className="text-zinc-600">
            Email {formatStatusLabel(status)}
          </span>
          {emailStatus.front_conversation_url && (
            <a
              href={emailStatus.front_conversation_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
            >
              View <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      );
    }
  }

  return null;
};

// Compact Alternates - shows alternate contacts from follow-up calls
interface CompactAlternatesProps {
  alternates: AlternateContact[];
  isLoading: boolean;
  onAccept: (alternate: AlternateContact) => Promise<void>;
  isAnyResendInFlight: boolean;
  activeAlternateKey: string | null;
  currentChannel?: {
    type?: string | null;
    fax?: string | null;
    email?: string | null;
    address?: string | null;
  } | null;
}

// Helper to check if an alternate matches the current channel
const isAlternateCurrentChannel = (
  alternate: AlternateContact,
  channel?: CompactAlternatesProps['currentChannel'],
): boolean => {
  if (!channel) return false;

  const handleType = canonicalizeHandleType(alternate.handle_type);
  const alternateValue = alternate.value?.replace(/\D/g, ''); // Normalize for comparison

  // Match based on channel type and value
  if (handleType === 'FAX' && channel.type === 'fax') {
    const channelFax = channel.fax?.replace(/\D/g, '');
    return Boolean(
      alternateValue && channelFax && alternateValue === channelFax,
    );
  }
  if (handleType === 'EMAIL' && channel.type === 'email') {
    return Boolean(
      alternate.value &&
        channel.email &&
        alternate.value.toLowerCase() === channel.email.toLowerCase(),
    );
  }
  if (handleType === 'MAILING_ADDRESS' && channel.type === 'mail') {
    // For addresses, do a simple includes check since formatting varies
    return Boolean(
      alternate.value &&
        channel.address &&
        (alternate.value
          .toLowerCase()
          .includes(channel.address.toLowerCase()) ||
          channel.address
            .toLowerCase()
            .includes(alternate.value.toLowerCase())),
    );
  }

  return false;
};

const CompactAlternates = ({
  alternates,
  isLoading,
  onAccept,
  isAnyResendInFlight,
  activeAlternateKey,
  currentChannel,
}: CompactAlternatesProps) => {
  const [pendingAlternate, setPendingAlternate] =
    useState<AlternateContact | null>(null);

  const handleUseClick = (alt: AlternateContact) => {
    setPendingAlternate(alt);
  };

  const handleConfirm = () => {
    if (pendingAlternate) {
      onAccept(pendingAlternate);
      setPendingAlternate(null);
    }
  };

  const handleCancel = () => {
    setPendingAlternate(null);
  };

  if (isLoading) {
    return (
      <div className="text-xs text-zinc-500 py-1 border-t border-zinc-100 pt-2 mt-2">
        <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
        Loading alternate contacts...
      </div>
    );
  }

  if (alternates.length === 0) {
    return null;
  }

  return (
    <>
      <Disclosure>
        {({ open }) => (
          <div className="border-t border-zinc-100 pt-2 mt-2">
            <Disclosure.Button className="flex w-full items-center justify-between text-xs py-1">
              <div className="flex items-center gap-1.5">
                <Phone className="h-3 w-3 text-amber-600" />
                <span className="font-medium text-amber-700">
                  {alternates.length} Alternate Contact
                  {alternates.length !== 1 ? 's' : ''}
                </span>
              </div>
              <ChevronDown
                className={`h-3 w-3 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="mt-2 space-y-1.5">
              {alternates.map((alt) => {
                const key = `${canonicalizeHandleType(alt.handle_type)}::${alt.value}`;
                const isProcessing = activeAlternateKey === key;
                const isCurrent = isAlternateCurrentChannel(
                  alt,
                  currentChannel,
                );
                // Only allow accepting FAX or MAILING_ADDRESS types
                const canAccept =
                  !isCurrent &&
                  ['FAX', 'MAILING_ADDRESS'].includes(
                    canonicalizeHandleType(alt.handle_type),
                  );

                // Confidence tier styling (matching EnrichmentConfidenceBadge)
                const confidence = alt.confidence ?? 0;
                const confidenceTier =
                  confidence >= 0.7
                    ? 'high'
                    : confidence >= 0.4
                      ? 'medium'
                      : 'low';
                const confidenceTierStyles = {
                  high: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                  medium: 'bg-amber-100 text-amber-700 border-amber-200',
                  low: 'bg-red-100 text-red-700 border-red-200',
                };

                return (
                  <div
                    key={key}
                    className="flex items-center justify-between py-1.5 px-2 rounded text-xs bg-zinc-50"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {getHandleIcon(alt.handle_type)}
                      <span className="truncate">{alt.value}</span>
                      {alt.confidence !== null &&
                        alt.confidence !== undefined && (
                          <span
                            className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap flex-shrink-0 ${confidenceTierStyles[confidenceTier]}`}
                          >
                            {Math.round(alt.confidence * 100)}% Confident
                          </span>
                        )}
                      {isCurrent && (
                        <span className="inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap flex-shrink-0 bg-green-100 text-green-700 border-green-200">
                          Current
                        </span>
                      )}
                    </div>
                    {canAccept && (
                      <button
                        type="button"
                        onClick={() => handleUseClick(alt)}
                        disabled={isAnyResendInFlight || isProcessing}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50 flex-shrink-0 ml-2"
                      >
                        {isProcessing ? (
                          <span className="flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Using...
                          </span>
                        ) : (
                          'Use'
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>

      <AlertDialog
        open={pendingAlternate !== null}
        onOpenChange={(open) => !open && handleCancel()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Use Alternate Contact?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                You are about to send a new request via the alternate contact:
              </p>
              {pendingAlternate && (
                <p className="font-medium text-foreground">
                  {pendingAlternate.value}
                </p>
              )}
              <p>
                This will cancel the current request and create a new one. The
                open task will be relinked to the new request.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isAnyResendInFlight}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isAnyResendInFlight}
            >
              {isAnyResendInFlight ? 'Processing...' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Channel Editor Modal - allows editing request channel type and details
interface ChannelEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
}

const ChannelEditorModal = ({
  isOpen,
  onClose,
  onSave,
  request,
  provider,
}: ChannelEditorModalProps) => {
  const { matterId } = useMatter();
  const [editedChannel, setEditedChannel] =
    useState<UpdateTreatmentRecordRequestChannelSchema>({});

  const [updateChannel, { isLoading: isUpdating }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();
  const [createChannel, { isLoading: isCreating }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsCreateMutation();

  const isLoading = isUpdating || isCreating;

  const suggestedFax = useMemo(() => {
    const fromHandles =
      provider?.handles?.find((h) => h.handle_type === 'FAX')?.value || '';
    const deprecatedField = provider?.fax || '';
    return (fromHandles || deprecatedField).trim();
  }, [provider?.handles, provider?.fax]);

  const suggestedAddress = useMemo(() => {
    const fromHandles =
      provider?.handles?.find((h) => h.handle_type === 'MAILING_ADDRESS')
        ?.value || '';
    const fallback = provider?.address || '';
    return (fromHandles || fallback).trim();
  }, [provider?.handles, provider?.address]);

  useEffect(() => {
    if (request?.channel) {
      setEditedChannel({
        type: request.channel.type,
        email: request.channel.email || undefined,
        fax:
          request.channel.fax ||
          (request.channel.type === 'fax'
            ? suggestedFax || undefined
            : undefined),
        address:
          request.channel.address ||
          (request.channel.type === 'mail'
            ? suggestedAddress || undefined
            : undefined),
        phone_number: request.channel.phone_number || undefined,
        instructions: request.channel.instructions || undefined,
      });
    }
  }, [request.channel, suggestedFax, suggestedAddress]);

  const handleSave = async () => {
    try {
      if (!request.channel && editedChannel.type) {
        await createChannel({
          matterId,
          providerId: provider.id,
          requestId: request.id,
          createTreatmentRecordRequestChannelSchema: {
            type: editedChannel.type,
            email: editedChannel.email,
            fax: editedChannel.fax,
            address: editedChannel.address,
            phone_number: editedChannel.phone_number,
            instructions: editedChannel.instructions,
          },
        }).unwrap();
        toast.success('Channel created');
      } else if (request.channel) {
        await updateChannel({
          matterId,
          providerId: provider.id,
          requestId: request.id,
          channelId: request.channel.id,
          updateTreatmentRecordRequestChannelSchema: editedChannel,
        }).unwrap();
        toast.success('Channel updated');
      }
      onSave?.();
      onClose();
    } catch {
      toast.error('Failed to update channel');
    }
  };

  return (
    <CatalystDialog
      open={isOpen}
      onClose={onClose}
      size="lg"
      zIndex={Z_INDEX_MODAL_BASE}
    >
      <DialogBody>
        <CatalystDialogTitle>Edit Request Channel</CatalystDialogTitle>
        <DialogDescription>
          Update how records will be requested from {provider.name}
        </DialogDescription>
        <div className="mt-4 space-y-3">
          <RequestMethodSelector
            value={editedChannel.type || request.channel?.type}
            onChange={(newType) =>
              setEditedChannel({
                ...editedChannel,
                type: newType,
                fax:
                  newType === 'fax'
                    ? editedChannel.fax || suggestedFax || undefined
                    : undefined,
                address:
                  newType === 'mail'
                    ? editedChannel.address || suggestedAddress || undefined
                    : undefined,
              })
            }
            className="w-full"
            inModal
          />

          {editedChannel.type === 'email' && (
            <Input
              type="email"
              value={editedChannel.email || ''}
              onChange={(e) =>
                setEditedChannel({ ...editedChannel, email: e.target.value })
              }
              placeholder="Email address"
            />
          )}

          {editedChannel.type === 'fax' && (
            <Input
              type="tel"
              value={editedChannel.fax || ''}
              onChange={(e) =>
                setEditedChannel({ ...editedChannel, fax: e.target.value })
              }
              placeholder="Fax number"
            />
          )}

          {editedChannel.type === 'mail' && (
            <textarea
              value={editedChannel.address || ''}
              onChange={(e) =>
                setEditedChannel({ ...editedChannel, address: e.target.value })
              }
              placeholder="Mailing address"
              className="w-full px-3 py-2 border border-zinc-950/10 rounded-lg resize-none bg-transparent focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          )}

          <Input
            type="text"
            value={editedChannel.instructions || ''}
            onChange={(e) =>
              setEditedChannel({
                ...editedChannel,
                instructions: e.target.value,
              })
            }
            placeholder="Instructions (optional)"
          />
        </div>
      </DialogBody>
      <DialogActions>
        <CatalystButton outline onClick={onClose} disabled={isLoading}>
          Cancel
        </CatalystButton>
        <CatalystButton onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </CatalystButton>
      </DialogActions>
    </CatalystDialog>
  );
};

// Interactive status dropdown for provider treatment status
const TreatmentStatusDropdown = ({
  provider,
}: {
  provider: MedicalProvider;
}) => {
  const { matterId } = useMatter();
  const [updateProvider] = useUpdateMedicalProviderMutation();

  const statusToColor: Record<ProviderTreatmentStatus, ColorVariant> = {
    [ProviderTreatmentStatus.TREATING]: 'purple',
    [ProviderTreatmentStatus.COMPLETED]: 'green',
    [ProviderTreatmentStatus.REFERRED_TO]: 'blue',
    [ProviderTreatmentStatus.DEVIATING]: 'red',
    [ProviderTreatmentStatus.UNKNOWN]: 'gray',
    [ProviderTreatmentStatus.NEVER_TREATED]: 'red',
  };

  const handleStatusChange = (newStatus: ProviderTreatmentStatus) => {
    updateProvider({
      matterId,
      providerId: provider.id,
      provider: {
        treatment_status: newStatus,
      },
    });
  };

  return (
    <Dropdown>
      <Dropdown.Button
        variant={statusToColor[provider.treatment_status]}
        className="flex flex-row gap-1 items-center justify-center text-[10px] py-0.5 px-2"
      >
        <span>{toTitleCase(provider.treatment_status)}</span>
        <ChevronDown className="w-3 h-3" />
      </Dropdown.Button>
      <Dropdown.Items>
        {Object.values(ProviderTreatmentStatus).map((status) => (
          <Dropdown.Item
            key={status}
            onClick={() => handleStatusChange(status)}
          >
            <span className="text-xs">{toTitleCase(status)}</span>
          </Dropdown.Item>
        ))}
      </Dropdown.Items>
    </Dropdown>
  );
};

// Interactive care level dropdown (single source of truth: MedicalProvider.care_level)
const CareLevelDropdown = ({ provider }: { provider: MedicalProvider }) => {
  const { matterId } = useMatter();
  const [updateProvider] = useUpdateMedicalProviderMutation();

  const careLevelLabel: Record<TreatmentCareLevel | 'UNCATEGORIZED', string> = {
    [TreatmentCareLevel.EMERGENCY_CARE]: 'Emergency',
    [TreatmentCareLevel.CONSERVATIVE_CARE]: 'Conservative',
    [TreatmentCareLevel.DIAGNOSTIC_TESTING]: 'Diagnostic',
    [TreatmentCareLevel.SPECIALIST_CARE]: 'Specialist',
    [TreatmentCareLevel.SURGICAL_CARE]: 'Surgical',
    UNCATEGORIZED: 'Uncategorized',
  };

  const currentKey = provider.care_level ?? 'UNCATEGORIZED';

  const handleCareLevelChange = async (newLevel: TreatmentCareLevel | null) => {
    try {
      await updateProvider({
        matterId,
        providerId: provider.id,
        provider: { care_level: newLevel },
      }).unwrap();
      toast.success('Care level updated');
    } catch {
      toast.error('Failed to update care level');
    }
  };

  const options: Array<{
    key: TreatmentCareLevel | 'UNCATEGORIZED';
    value: TreatmentCareLevel | null;
  }> = [
    {
      key: TreatmentCareLevel.EMERGENCY_CARE,
      value: TreatmentCareLevel.EMERGENCY_CARE,
    },
    {
      key: TreatmentCareLevel.CONSERVATIVE_CARE,
      value: TreatmentCareLevel.CONSERVATIVE_CARE,
    },
    {
      key: TreatmentCareLevel.DIAGNOSTIC_TESTING,
      value: TreatmentCareLevel.DIAGNOSTIC_TESTING,
    },
    {
      key: TreatmentCareLevel.SPECIALIST_CARE,
      value: TreatmentCareLevel.SPECIALIST_CARE,
    },
    {
      key: TreatmentCareLevel.SURGICAL_CARE,
      value: TreatmentCareLevel.SURGICAL_CARE,
    },
    { key: 'UNCATEGORIZED', value: null },
  ];

  return (
    <Dropdown>
      <Dropdown.Button
        variant="gray"
        className="flex flex-row gap-1 items-center justify-center text-[10px] py-0.5 px-2"
      >
        <span>{careLevelLabel[currentKey]}</span>
        <ChevronDown className="w-3 h-3" />
      </Dropdown.Button>
      <Dropdown.Items>
        {options.map((opt) => (
          <Dropdown.Item
            key={opt.key}
            onClick={() => handleCareLevelChange(opt.value)}
          >
            <span className="text-xs">{careLevelLabel[opt.key]}</span>
          </Dropdown.Item>
        ))}
      </Dropdown.Items>
    </Dropdown>
  );
};

// Progress steps configuration with CTA actions lives in `recordRequestProgress.ts`.

// Format record types for display
const formatRecordTypes = (types: TreatmentRecordTypes[]): string => {
  if (!types || types.length === 0) return 'Not specified';
  return types.join(', ');
};

// Compact request row (collapsed state)
interface RequestRowProps {
  request: TreatmentRecordRequestSchema;
  isExpanded: boolean;
  onToggle: () => void;
  isSuperseded?: boolean;
  matterId: string;
  providerId: string;
}

const RequestRow = ({
  request,
  isExpanded,
  onToggle,
  isSuperseded,
  matterId,
  providerId,
}: RequestRowProps) => {
  const navigate = useNavigate();
  const recordTypes = formatRecordTypes(request.provided_records ?? []);
  const status = request.status ?? 'not_ready';
  const statusInfo = StatusToLabel[status];
  const channelType = request.channel?.type;
  const originBadge = getRequestOrigin(request);

  // Get metadata for auto_retried tooltip and submission date
  const extendedMetadata = getExtendedMetadata(request.metadata);

  // Get accurate HIPAA sent date from transmission status fields
  // Returns null for historical records without accurate data
  const hipaaSentAt = getHipaaSentDate(request);

  // For display: show sent date if available, otherwise just show created date
  const dateLabel = hipaaSentAt
    ? `Sent ${format(new Date(hipaaSentAt), 'MMM d, yyyy')}`
    : request.created_at
      ? format(new Date(request.created_at), 'MMM d, yyyy')
      : '';

  // Build submission tooltip content
  const submissionChannel =
    extendedMetadata.hipaa_package_submission_channel || channelType;
  // Show portal system name (e.g. "ChartSwap") instead of generic "Portal"
  const portalSystemName = request.channel?.portal_provider?.portal
    ? request.channel.portal_provider.portal === 'chartswap'
      ? 'ChartSwap'
      : request.channel.portal_provider.portal === 'smart_request'
        ? 'SmartRequest'
        : request.channel.portal_provider.portal === 'mro'
          ? 'MRO'
          : request.channel.portal_provider.portal
    : null;
  const portalLabel = portalSystemName ? `Portal (${portalSystemName})` : null;
  const channelDisplay = submissionChannel
    ? submissionChannel === 'portal' && portalLabel
      ? portalLabel
      : ChannelTypeLabels[submissionChannel] || submissionChannel
    : null;
  const dateTooltipContent = (
    <div className="space-y-1 text-xs">
      {request.created_at && (
        <p>Created: {format(new Date(request.created_at), 'MMM d, yyyy')}</p>
      )}
      {hipaaSentAt && (
        <p>
          Submitted{channelDisplay ? ` via ${channelDisplay}` : ''}:{' '}
          {format(new Date(hipaaSentAt), 'MMM d, yyyy h:mm a')}
        </p>
      )}
    </div>
  );
  const isAutoRetriedStatus = status === 'auto_retried';

  // Format the fax number if available
  const alternateContact = extendedMetadata.alternate_contact;
  const formattedFaxValue =
    alternateContact?.handle_type === 'fax' && alternateContact?.value
      ? formatPhoneNumberForDisplay(alternateContact.value)
      : alternateContact?.value;

  // Handle click on "View new request" link
  const handleViewNewRequest = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (extendedMetadata.forked_to_request_id) {
      navigate(
        `/matters/${matterId}/treatment?request_id=${extendedMetadata.forked_to_request_id}&provider_id=${providerId}`,
      );
    }
  };

  // Tooltip content for auto_retried status
  const autoRetriedTooltipContent = (
    <div className="space-y-2 max-w-xs">
      <p className="font-medium">Auto-Retried</p>
      <p className="text-xs opacity-80">
        Voice AI discovered a different fax number during a provider call.
      </p>
      {formattedFaxValue && (
        <p className="text-xs">
          <span className="opacity-70">New Fax:</span>{' '}
          <span className="font-medium">{formattedFaxValue}</span>
        </p>
      )}
      {extendedMetadata.forked_to_request_id && (
        <button
          type="button"
          onClick={handleViewNewRequest}
          className="inline-flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200 font-medium"
        >
          <ArrowRight className="h-3 w-3" />
          View new request
        </button>
      )}
    </div>
  );

  // Render the status badge
  const statusBadge = (
    <Badge color={statusInfo.color} className="text-[10px]">
      {statusInfo.label}
    </Badge>
  );

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors border-b border-zinc-200 min-w-0 ${
        isExpanded ? 'bg-zinc-100' : 'hover:bg-zinc-50'
      } ${isSuperseded ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <ChevronRight
          className={`h-3.5 w-3.5 text-zinc-400 transition-transform shrink-0 ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />
        <span
          className={`text-xs font-medium min-w-0 flex-1 truncate ${
            isSuperseded ? 'text-zinc-400' : 'text-zinc-900'
          }`}
        >
          {recordTypes}
        </span>
        {originBadge &&
          (originBadge.tooltip ? (
            <Tooltip content={originBadge.tooltip} variant="dark" side="top">
              <span className="shrink-0 cursor-help">
                <Zap className="h-3.5 w-3.5 text-amber-500" />
              </span>
            </Tooltip>
          ) : (
            <Badge color={originBadge.color} className="text-[9px] shrink-0">
              {originBadge.label}
            </Badge>
          ))}
        {dateLabel && (
          <Tooltip content={dateTooltipContent} variant="dark" side="top">
            <span className="text-[10px] text-zinc-400 cursor-help shrink-0 hidden sm:inline">
              {dateLabel}
            </span>
          </Tooltip>
        )}
        <FollowUpBadge
          matterId={matterId}
          providerId={providerId}
          request={request}
        />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isAutoRetriedStatus ? (
          <Tooltip
            content={autoRetriedTooltipContent}
            variant="dark"
            side="top"
          >
            <span className="cursor-help">{statusBadge}</span>
          </Tooltip>
        ) : (
          statusBadge
        )}
      </div>
    </button>
  );
};

// Expanded request detail view
interface RequestDetailProps {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
  onCollapse: () => void;
  onDelete: () => void;
}

const RequestDetail = ({
  request,
  provider,
  onCollapse,
  onDelete,
}: RequestDetailProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { matterId } = useMatter();
  const dispatch = useDispatch();
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [isChannelEditorOpen, setIsChannelEditorOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Invoice modal state
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceSelectedBlobIds, setInvoiceSelectedBlobIds] = useState<
    string[]
  >([]);
  const [isInvoiceDocModalOpen, setIsInvoiceDocModalOpen] = useState(false);

  // Records upload modal state
  const [isRecordsModalOpen, setIsRecordsModalOpen] = useState(false);

  // Fax submission modal state
  const [isFaxModalOpen, setIsFaxModalOpen] = useState(false);
  const [faxCoverTo, setFaxCoverTo] = useState('');
  const [faxCoverFrom, setFaxCoverFrom] = useState('');
  const [faxCoverMessage, setFaxCoverMessage] = useState('');
  const [isFaxSubmitting, setIsFaxSubmitting] = useState(false);

  // Email submission modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSenderOption, setEmailSenderOption] =
    useState<'firm' | 'records'>('firm');
  const [emailValidationError, setEmailValidationError] = useState<
    string | null
  >(null);
  const [emailTemplateInitialized, setEmailTemplateInitialized] =
    useState(false);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);

  // Mail coming soon modal state
  const [isMailComingSoonOpen, setIsMailComingSoonOpen] = useState(false);

  const [changeStatus] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChangeStatusCreateMutation();
  const [deleteRequest] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsDestroyMutation();
  const [createInvoice, { isLoading: isSubmittingInvoice }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsInvoicesCreateMutation();
  const [submitHipaaPackage] =
    treatmentRecordRequestService.useSubmitHipaaPackageMutation();

  const { handleUploadToCollection } = useUploadManagerUnified({
    matterId,
    refetch: () => {},
  });

  // Fetch matter data to get firm ID (for email Front channel)
  const { data: matterData } = mattersApiClient.useGetMatterQuery(matterId);
  const firmId = matterData?.firm?.id;

  // Fetch full firm data to access Front channel
  const { data: firmData } = firmsApiClient.useGetFirmQuery(firmId || '', {
    skip: !firmId,
  });
  const firmFrontChannel = firmData?.front_channel;

  // Fetch package data
  const { data: packageData } = useGetPackageQuery(
    { matterId, providerId: provider.id, requestId: request.id },
    { skip: !request.id },
  );

  // Check HIPAA package availability for gating fax/email/mail buttons
  const { data: hipaaPackage } =
    hipaaPackageApi.useRequestHipaaPackageRetrieveQuery(
      { matterId, providerId: provider.id, requestId: request.id },
      {
        skip:
          request.channel?.type !== 'fax' &&
          request.channel?.type !== 'email' &&
          request.channel?.type !== 'mail',
      },
    );
  const isHipaaPackageReady = Boolean(hipaaPackage?.completed_package_id);

  // Email template query (only when email modal is open)
  const emailTemplateQueryArgs = useMemo(
    () => ({ matterId, providerId: provider.id, requestId: request.id }),
    [matterId, provider.id, request.id],
  );
  const {
    data: emailTemplate,
    isFetching: isEmailTemplateFetching,
    isLoading: isEmailTemplateLoading,
    isError: isEmailTemplateError,
  } = treatmentRecordRequestService.useGetHipaaEmailTemplateQuery(
    emailTemplateQueryArgs,
    { skip: !isEmailModalOpen },
  );

  // Initialize email template when loaded
  useEffect(() => {
    if (!isEmailModalOpen) {
      setEmailSubject('');
      setEmailBody('');
      setEmailValidationError(null);
      setEmailTemplateInitialized(false);
      setEmailSenderOption('firm');
      return;
    }
    if (emailTemplate && !emailTemplateInitialized) {
      setEmailSubject(emailTemplate.subject);
      setEmailBody(emailTemplate.body);
      setEmailTemplateInitialized(true);
      setEmailValidationError(null);
    } else if (isEmailTemplateError && !emailTemplateInitialized) {
      setEmailTemplateInitialized(true);
    }
  }, [
    emailTemplate,
    emailTemplateInitialized,
    isEmailTemplateError,
    isEmailModalOpen,
  ]);

  // Auto-select records sender when firm channel is not configured
  useEffect(() => {
    if (!isEmailModalOpen) return;
    if (!firmFrontChannel?.address && RECORDS_FRONT_CHANNEL_ID.trim()) {
      setEmailSenderOption('records');
    } else {
      setEmailSenderOption('firm');
    }
  }, [firmFrontChannel?.address, isEmailModalOpen]);

  // Get extended metadata for Voice AI info
  const extendedMetadata = useMemo(
    () => getExtendedMetadata(request.metadata),
    [request.metadata],
  );

  // Voice AI alternate contacts from follow-up calls
  const {
    alternates,
    collection: alternatesCollection,
    isLoading: isAlternatesLoading,
  } = useAlternateContacts(matterId, request.task_id, request.metadata);

  // Fax transmission status
  const {
    faxStatus,
    refetch: refetchFaxRequest,
    isLoading: isFaxLoading,
    isTerminalStatus: isFaxTerminal,
  } = useRequestFaxStatus({
    matterId,
    providerId: provider.id,
    requestId: request.id,
    enabled: request.channel?.type === 'fax',
  });

  // Use the faxService mutation for actual refresh (this is what works in the Fax Table)
  const [refreshFaxStatusMutation, { isLoading: isRefreshingFaxStatus }] =
    useRefreshFaxStatusMutation();

  // Get the fax transmission database ID from the status (for refresh API)
  // Note: `id` is newly added to FaxTransmissionStatusSchema but not yet in generated types
  const faxTransmissionId = (faxStatus as { id?: string } | undefined)?.id;

  // Handle refresh using the correct API
  const handleRefreshFax = async () => {
    if (faxTransmissionId) {
      try {
        await refreshFaxStatusMutation(faxTransmissionId).unwrap();
        toast.success('Fax status refreshed');
        // Also refetch the request to get updated status
        try {
          await refetchFaxRequest();
        } catch (refetchError) {
          console.error(
            'Failed to refetch request after refresh:',
            refetchError,
          );
          // Status was refreshed but UI may be stale
        }
      } catch (error) {
        console.error('Failed to refresh fax status:', error);
        toast.error('Failed to refresh fax status');
      }
    } else {
      // Log missing transmission_id for debugging - this indicates a backend data issue
      console.warn('Cannot refresh fax status: transmission_id not available', {
        requestId: request.id,
        faxStatus,
      });
      // Still refetch to get latest data, but inform user refresh may not work
      await refetchFaxRequest();
      toast.info(
        'Unable to refresh from carrier - transmission ID not available',
      );
    }
  };

  // Auto-refresh fax status every 30 seconds if not delivered/failed
  useEffect(() => {
    if (request.channel?.type !== 'fax' || isFaxTerminal) {
      return undefined;
    }
    const interval = setInterval(() => {
      if (faxTransmissionId) {
        refreshFaxStatusMutation(faxTransmissionId);
      }
      refetchFaxRequest();
    }, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [
    request.channel?.type,
    isFaxTerminal,
    faxTransmissionId,
    refreshFaxStatusMutation,
    refetchFaxRequest,
  ]);

  // Email submission status
  const { data: emailStatus, isFetching: isEmailStatusLoading } =
    treatmentRecordRequestService.useGetHipaaSubmissionStatusQuery(
      { matterId, providerId: provider.id, requestId: request.id },
      { skip: request.channel?.type !== 'email' },
    );

  // Resend actions hook for alternate contacts and current contact resend
  const { resendWithAlternate, isAnyResendInFlight, activeAlternateKey } =
    useRequestResendActions({
      matterId,
      providerId: provider.id,
      requestId: request.id,
    });

  // Check if request has Voice AI data
  const hasVoiceAIData = useMemo(
    () => hasVoiceAIContactData(request.metadata),
    [request.metadata],
  );

  // Check if follow-up alternates exist (these supersede initial Voice AI discovery)
  const hasFollowUpAlternates = Boolean(
    alternatesCollection?.alternates?.length,
  );

  const currentStatus = request.status ?? 'not_ready';
  const currentStepIndex = getStepIndex(currentStatus);
  const statusInfo = StatusToLabel[currentStatus];

  const isRejected = currentStatus === 'rejected';
  const isCancelled = currentStatus === 'cancelled';
  const isAutoRetried = currentStatus === 'auto_retried';
  const isComplete = currentStatus === 'records_ingested';
  const isTerminal = isRejected || isCancelled || isAutoRetried || isComplete;

  // Check for Voice AI automation blockers
  const automationState = request.metadata?.automation_state_v2;
  const isBlocked = automationState?.status === 'blocked';
  const hasBlockers =
    isBlocked &&
    Array.isArray(automationState?.blocked_by) &&
    automationState.blocked_by.length > 0;

  const visualStatusInfo = statusInfo;
  // No visual step advancement - use actual step index
  const visualStepIndex = currentStepIndex;

  const totalSteps = PROGRESS_STEPS.length;
  const nextStepIndex = getNextRequestProgressIndex({
    currentStatusIndex: currentStepIndex,
    isTerminal,
  });

  const channelType = request.channel?.type;
  // Channel button logic:
  // - No channel type → "Select" in red
  // - Channel type but no value → "Fax"/"Email" in red
  // - Channel type + value → "Fax"/"Email" normal
  // Show portal system name in channel label when available
  const detailPortalSystem = request.channel?.portal_provider?.portal
    ? request.channel.portal_provider.portal === 'chartswap'
      ? 'ChartSwap'
      : request.channel.portal_provider.portal === 'smart_request'
        ? 'SmartRequest'
        : request.channel.portal_provider.portal === 'mro'
          ? 'MRO'
          : request.channel.portal_provider.portal
    : null;
  const channelLabel = channelType
    ? channelType === 'portal' && detailPortalSystem
      ? `Portal (${detailPortalSystem})`
      : ChannelTypeLabels[channelType] || channelType
    : 'Select';
  const hasChannelType = Boolean(channelType);
  const hasChannelValue = Boolean(
    (channelType === 'fax' && request.channel?.fax?.trim()) ||
      (channelType === 'email' && request.channel?.email?.trim()) ||
      (channelType === 'mail' && request.channel?.address),
  );
  const isChannelIncomplete = !hasChannelType || !hasChannelValue;
  const originBadge = getRequestOrigin(request);

  // Handle status change
  const handleStatusChange = async (
    newStatus: TreatmentRecordRequestStatus,
    attachments?: string[],
  ) => {
    if (newStatus === request.status) return;

    if (
      newStatus === 'records_ingested' &&
      (!attachments || !attachments.length)
    ) {
      setIsRecordsModalOpen(true);
      return;
    }

    setIsChangingStatus(true);
    try {
      await changeStatus({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        changeTreatmentRecordRequestStatusSchema: {
          new_status: newStatus,
          override_requirements: true,
          ...(attachments?.length ? { attachments } : {}),
        },
      }).unwrap();
      toast.success(
        newStatus === 'records_ingested' && attachments?.length
          ? 'Medical records uploaded and request completed'
          : 'Status updated',
      );
      if (newStatus === 'records_ingested') {
        setIsRecordsModalOpen(false);
      }
    } catch {
      toast.error(
        newStatus === 'records_ingested'
          ? 'Failed to complete request. Please try again.'
          : 'Failed to update status',
      );
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Bypass handler: firm already has the records physically
  const handleRecordsBypass = async () => {
    setIsChangingStatus(true);
    try {
      await changeStatus({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        changeTreatmentRecordRequestStatusSchema: {
          new_status: 'records_ingested',
          override_requirements: true,
          log: { details: 'Records already received by firm' },
        },
      }).unwrap();
      toast.success('Request completed — records already received by firm');
      setIsRecordsModalOpen(false);
    } catch {
      toast.error('Failed to complete request. Please try again.');
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Open package modal via URL params
  const handleOpenPackage = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('request_id', request.id);
    newSearchParams.set('provider_id', provider.id);
    navigate({ search: newSearchParams.toString() }, { replace: false });
  };

  // Handle delete request
  const handleDeleteRequest = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this request? This action cannot be undone.',
      )
    ) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteRequest({
        matterId,
        providerId: provider.id,
        requestId: request.id,
      }).unwrap();
      toast.success('Request deleted');
      onDelete();
    } catch {
      toast.error('Failed to delete request');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle blocker override - manually advance status bypassing blockers
  const handleOverrideBlockers = async () => {
    const nextStatus = PROGRESS_STEPS[nextStepIndex]?.status;
    if (nextStatus) {
      await handleStatusChange(nextStatus);
    }
  };

  // Get current step info for CTA (use currentStepIndex for CTA text/action)
  const currentStep = PROGRESS_STEPS[currentStepIndex];
  const nextStep = PROGRESS_STEPS[nextStepIndex];
  const ctaText = currentStep?.ctaText || 'Continue';
  const actionType = currentStep?.actionType || 'status_change';
  // Target status is the next step's status (where we're advancing to)
  const targetStatus = nextStep?.status;

  // Determine if we're at a status where send buttons should appear
  const isPendingStatus = currentStatus === 'pending';
  const isSendToProviderStatus = currentStatus === 'request_submitted';
  const isFaxChannel = request.channel?.type === 'fax';
  const isEmailChannel = request.channel?.type === 'email';
  const isMailChannel = request.channel?.type === 'mail';

  // Check if fax/email has already been sent
  const isFaxAlreadySent =
    isFaxChannel &&
    (faxStatus?.status === 'sent' || faxStatus?.status === 'delivered');
  const isEmailAlreadySent = isEmailChannel && Boolean(emailStatus?.sent_at);

  // Show send buttons only before submission:
  // - pending + package approved/ready (fax/email only)
  // Hide once fax/email already has a send status.
  const showSendButtons =
    isPendingStatus &&
    isHipaaPackageReady &&
    (isFaxChannel || isEmailChannel) &&
    !isFaxAlreadySent &&
    !isEmailAlreadySent;
  // Show "Prepare HIPAA" button when pending but package not ready
  const showPrepareHipaa = isPendingStatus && !isHipaaPackageReady;

  // Validation for send buttons
  const isFaxMissing = isFaxChannel && !request.channel?.fax?.trim();
  const isEmailMissing = isEmailChannel && !request.channel?.email?.trim();
  const isFirmChannelMissing = isEmailChannel && !firmFrontChannel?.address;
  const canSendFax = isFaxChannel && !isFaxMissing && isHipaaPackageReady;
  const canSendEmail = isEmailChannel && !isEmailMissing && isHipaaPackageReady;
  const hasChannel = Boolean(request.channel);

  // Handle CTA button click based on action type
  const handleCtaClick = async () => {
    if (!targetStatus) return;

    switch (actionType) {
      case 'channel_submit':
        // For channel_submit, we now use the dedicated send buttons
        // This fallback handles non-fax/email/mail channels
        await handleStatusChange(targetStatus);
        break;
      case 'invoice_upload':
        setIsInvoiceModalOpen(true);
        break;
      case 'records_upload':
        setIsRecordsModalOpen(true);
        break;
      case 'status_change':
      default:
        // Simple status change
        await handleStatusChange(targetStatus);
        break;
    }
  };

  // Handle fax submission
  const handleSendFax = async () => {
    if (!canSendFax) {
      if (isFaxMissing) {
        toast.error('Please add a fax number before sending.');
      } else if (!isHipaaPackageReady) {
        toast.error('Please complete the HIPAA package before sending.');
      }
      return;
    }
    setIsFaxModalOpen(true);
  };

  const handleConfirmFaxSubmit = async () => {
    setIsFaxSubmitting(true);
    try {
      const payload: SubmitHipaaPackageSchema = { channel_type: 'fax' };
      if (faxCoverTo.trim()) payload.cover_page_to = faxCoverTo.trim();
      if (faxCoverFrom.trim()) payload.cover_page_from = faxCoverFrom.trim();
      if (faxCoverMessage.trim())
        payload.cover_page_message = faxCoverMessage.trim();

      await submitHipaaPackage({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        submitHipaaPackageSchema: payload,
      }).unwrap();

      toast.success('Fax submitted! Check transmission status for updates.');
      setIsFaxModalOpen(false);
      setFaxCoverTo('');
      setFaxCoverFrom('');
      setFaxCoverMessage('');

      // Keep request at "Send to Provider"; user confirms receipt separately.
      await handleStatusChange('request_submitted');
    } catch {
      toast.error('Failed to send fax. Please try again.');
    } finally {
      setIsFaxSubmitting(false);
    }
  };

  // Handle email submission
  const handleSendEmail = async () => {
    if (!canSendEmail) {
      if (isEmailMissing) {
        toast.error('Please add an email address before sending.');
      } else if (!isHipaaPackageReady) {
        toast.error('Please complete the HIPAA package before sending.');
      }
      return;
    }
    setIsEmailModalOpen(true);
  };

  const handleConfirmEmailSubmit = async () => {
    const trimmedSubject = emailSubject.trim();
    const trimmedBody = emailBody.trim();
    if (!trimmedSubject || !trimmedBody) {
      setEmailValidationError('Subject and body are required.');
      return;
    }

    if (emailSenderOption === 'records' && !RECORDS_FRONT_CHANNEL_ID.trim()) {
      toast.error('Records sender channel is not configured');
      return;
    }

    if (emailSenderOption === 'firm' && !firmFrontChannel?.address) {
      toast.error('Firm Front channel is not configured');
      return;
    }

    setEmailValidationError(null);
    setIsEmailSubmitting(true);
    try {
      const payload: SubmitHipaaPackageSchema & { front_channel_id?: string } = {
        channel_type: 'email',
        email_subject: trimmedSubject,
        email_body: trimmedBody,
      };
      if (emailSenderOption === 'records') {
        payload.front_channel_id = RECORDS_FRONT_CHANNEL_ID.trim();
      }

      await submitHipaaPackage({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        submitHipaaPackageSchema: payload,
      }).unwrap();

      toast.success('Email sent successfully!');
      setIsEmailModalOpen(false);
      setEmailSubject('');
      setEmailBody('');

      // Keep request at "Send to Provider"; user confirms receipt separately.
      await handleStatusChange('request_submitted');
    } catch {
      toast.error('Failed to send email. Please try again.');
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  // Handle mail (coming soon)
  const handleSendMail = () => {
    setIsMailComingSoonOpen(true);
  };

  // Handle invoice submission
  const handleSubmitInvoice = async () => {
    try {
      if (invoiceSelectedBlobIds.length > 0 && invoiceAmount) {
        await createInvoice({
          matterId,
          providerId: provider.id,
          requestId: request.id,
          createTreatmentRecordRequestInvoiceSchema: {
            amount: Number(invoiceAmount),
            date_received: new Date().toISOString(),
            date_paid: new Date().toISOString(),
            invoice_file_id: invoiceSelectedBlobIds[0],
          },
        }).unwrap();
        toast.success('Invoice submitted');
      }
      // Progress to next status
      if (targetStatus) {
        await handleStatusChange(targetStatus);
      }
      setIsInvoiceModalOpen(false);
      setInvoiceAmount('');
      setInvoiceSelectedBlobIds([]);
    } catch {
      toast.error('Failed to submit invoice');
    }
  };

  // Handle skip invoice
  const handleSkipInvoice = async () => {
    if (targetStatus) {
      await handleStatusChange(targetStatus);
    }
    setIsInvoiceModalOpen(false);
  };

  // Determine package button state
  const getPackageButtonState = () => {
    if (!packageData) {
      return {
        text: 'View Package',
        icon: EyeIcon,
        variant: 'outline' as const,
      };
    }
    const hasMissingFields = packageData.components?.some((c) =>
      hasBlockingMissingFields(c.missing_fields),
    );
    // Only consider actual missing fields, not just the status string
    if (hasMissingFields) {
      return {
        text: 'Fix Missing Info',
        icon: ExclamationTriangleIcon,
        variant: 'default' as const,
      };
    }
    if (packageData.status === 'ready') {
      return {
        text: 'Approve Package',
        icon: CheckCircleIcon,
        variant: 'default' as const,
      };
    }
    return { text: 'View Package', icon: EyeIcon, variant: 'outline' as const };
  };

  const packageButtonState = getPackageButtonState();
  const PackageIcon = packageButtonState.icon;

  return (
    <div className="border border-zinc-200 rounded-lg bg-white min-w-0">
      {/* Header - compact */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 rounded-t-lg min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1">
          <button
            type="button"
            onClick={onCollapse}
            className="p-0.5 hover:bg-zinc-200 rounded transition-colors"
          >
            <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
          </button>
          <span className="text-xs font-medium text-zinc-900 min-w-0 flex-1 truncate">
            {formatRecordTypes(request.provided_records ?? [])}
          </span>
          {originBadge &&
            (originBadge.tooltip ? (
              <Tooltip content={originBadge.tooltip} variant="dark" side="top">
                <span className="shrink-0 cursor-help">
                  <Zap className="h-3.5 w-3.5 text-amber-500" />
                </span>
              </Tooltip>
            ) : (
              <Badge color={originBadge.color} className="text-[9px] shrink-0">
                {originBadge.label}
              </Badge>
            ))}
          <span className="text-[10px] text-zinc-400 shrink-0 hidden sm:inline">
            {request.created_at
              ? format(new Date(request.created_at), 'MMM d, yyyy')
              : ''}
          </span>
        </div>

        {/* Status dropdown (represents last completed status) */}
        <Menu as="div" className="relative ml-auto shrink-0">
          <Menu.Button as="div" className="cursor-pointer">
            <Badge
              color={statusInfo.color}
              className="inline-flex items-center gap-0.5 text-[9px]"
            >
              {statusInfo.label}
              <ChevronDownIcon className="h-2.5 w-2.5 opacity-50" />
            </Badge>
          </Menu.Button>
          <Menu.Items className="absolute right-0 mt-1 w-44 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-50">
            <div className="px-3 py-2 text-[10px] font-semibold text-zinc-500 uppercase border-b border-zinc-100">
              Set Last Completed
            </div>
            <div className="py-1 max-h-48 overflow-y-auto">
              {StatusOptions.map((opt) => (
                <Menu.Item key={opt.value}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => handleStatusChange(opt.value)}
                      disabled={
                        isChangingStatus || opt.value === request.status
                      }
                      className={`${active ? 'bg-zinc-50' : ''} ${
                        opt.value === request.status
                          ? 'text-zinc-400'
                          : 'text-zinc-700'
                      } flex w-full items-center px-3 py-1.5 text-xs disabled:cursor-not-allowed`}
                    >
                      {opt.label}
                      {opt.value === request.status && (
                        <span className="ml-auto text-[10px] text-zinc-400">
                          Current
                        </span>
                      )}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Menu>
      </div>

      {/* Content */}
      <div className="px-3 py-2.5 space-y-2.5">
        {/* Progress dots */}
        <div className="flex flex-col gap-1.5">
          {/* Dots and lines */}
          <div className="flex items-center justify-between px-1">
            {PROGRESS_STEPS.map((step, index) => {
              // Use visualStepIndex for display (advances when HIPAA is ready)
              // When complete, all steps including the last one should be marked completed
              const isCompleted =
                index < visualStepIndex ||
                (isComplete && index <= visualStepIndex);
              const isCurrent = !isTerminal && index === visualStepIndex;
              const stepStatus = step.status;
              const isAtStatus = stepStatus === currentStatus;
              const isAnimatedLineToCurrent =
                !isTerminal && index === visualStepIndex - 1;
              const stepName = step.label;
              let confirmLabel = 'Confirm';
              if (isChangingStatus) {
                confirmLabel = 'Updating...';
              }

              const getDotColor = () => {
                if (isRejected) return 'bg-red-500';
                if (isCancelled || isAutoRetried) return 'bg-zinc-400';
                if (isCompleted) return 'bg-zinc-900';
                if (isCurrent) return 'bg-violet-600';
                return 'bg-zinc-300';
              };

              const getLineColor = () => {
                if (isRejected) return 'bg-red-300';
                if (isCancelled || isAutoRetried) return 'bg-zinc-300';
                if (index < visualStepIndex) return 'bg-zinc-900';
                return 'bg-zinc-200';
              };

              return (
                <div
                  key={step.status}
                  className="flex items-center flex-1 last:flex-none"
                >
                  {/* Dot: current step (violet) has no popover */}
                  {isCurrent ? (
                    <div
                      className={`relative w-3 h-3 rounded-full transition-all ${getDotColor()} ring-2 ring-violet-200 ring-offset-1`}
                    />
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          disabled={isChangingStatus}
                          className={`relative w-3 h-3 rounded-full transition-all ${getDotColor()} ${
                            !isChangingStatus
                              ? 'hover:scale-125 hover:ring-2 hover:ring-zinc-300 hover:ring-offset-1 cursor-pointer'
                              : 'cursor-default'
                          }`}
                        >
                          {isCompleted && (
                            <Check className="absolute inset-0 w-3 h-3 text-white p-0.5" />
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-56 p-3"
                        side="top"
                        align="center"
                      >
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-zinc-900">
                            Set last completed to &quot;{stepName}&quot;?
                          </p>
                          <p className="text-[10px] text-zinc-500">
                            This will update the request status.
                          </p>
                          <div className="flex gap-2 pt-1">
                            <PopoverClose asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 h-7 text-xs"
                              >
                                Cancel
                              </Button>
                            </PopoverClose>
                            <Button
                              size="sm"
                              className="flex-1 h-7 text-xs"
                              disabled={isChangingStatus || isAtStatus}
                              onClick={() => handleStatusChange(stepStatus)}
                            >
                              {confirmLabel}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                  {/* Line (not after last dot) */}
                  {index < PROGRESS_STEPS.length - 1 &&
                    (isAnimatedLineToCurrent ? (
                      <div
                        className="flex-1 h-0.5 mx-1 overflow-hidden"
                        style={{
                          background: `repeating-linear-gradient(
                            90deg,
                            rgb(139 92 246) 0px,
                            rgb(139 92 246) 3px,
                            transparent 3px,
                            transparent 6px
                          )`,
                          backgroundSize: '200% 100%',
                          animation: 'dash-move 1s linear infinite',
                        }}
                      />
                    ) : (
                      <div className={`flex-1 h-0.5 mx-1 ${getLineColor()}`} />
                    ))}
                </div>
              );
            })}
          </div>
          {/* Current step label */}
          <div className="text-center">
            <div className="text-xs font-medium text-zinc-700">
              {visualStatusInfo.label}
            </div>
            {!isTerminal && (
              <div className="text-[10px] text-zinc-400">
                ({visualStepIndex + 1}/{totalSteps})
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-start gap-2 pt-2 border-t border-zinc-100 min-w-0">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar whitespace-nowrap min-w-0">
            {/* Channel badge/button - opens channel editor modal */}
            {/* Red when no channel or channel value is missing */}
            <BadgeButton
              color={isChannelIncomplete ? 'red' : 'zinc'}
              className="text-xs shrink-0"
              onClick={() => setIsChannelEditorOpen(true)}
            >
              {channelLabel}
            </BadgeButton>

            {/* Package button */}
            <Button
              variant={packageButtonState.variant}
              size="sm"
              onClick={handleOpenPackage}
              className="h-7 text-xs gap-1 shrink-0"
            >
              <PackageIcon className="h-3.5 w-3.5" />
              {packageButtonState.text}
            </Button>

            {/* Delete button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeleteRequest}
              disabled={isDeleting}
              className="h-7 text-xs shrink-0"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>

            {/* Resend button - icon only with tooltip (hide when preparing HIPAA since no transmission yet) */}
            {hasChannel && !showPrepareHipaa && (
              <Tooltip content="Resend request">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsResendModalOpen(true)}
                  disabled={isAnyResendInFlight}
                  className="h-7 w-7 p-0 shrink-0"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </Tooltip>
            )}
          </div>

          <div className="w-full sm:w-auto sm:ml-auto flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 min-w-0">
            {/* CTA buttons - contextual based on status and channel */}
            {isTerminal ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenPackage}
                className="h-7 text-xs w-full sm:w-auto"
              >
                View Details
              </Button>
            ) : hasBlockers ? (
              // Show blocker resolution button when request is blocked, with optional resend
              <ResolveBlockersButton
                request={request}
                blockedState={automationState as BlockedState}
                provider={provider}
                onOverride={handleOverrideBlockers}
                isDisabled={isChangingStatus}
                compact
                highZIndex
              />
            ) : (
              <>
                {/* Send Fax / Email / Mail buttons when pending */}
                {showSendButtons && isFaxChannel && (
                  <Tooltip
                    content={
                      !isHipaaPackageReady
                        ? 'Complete the HIPAA package first'
                        : isFaxMissing
                          ? 'Add a fax number first'
                          : 'Send HIPAA package via fax'
                    }
                  >
                    <span>
                      <Button
                        size="sm"
                        onClick={handleSendFax}
                        disabled={!canSendFax || isFaxSubmitting}
                        className="h-7 text-xs gap-1 bg-zinc-900 hover:bg-zinc-800 text-white disabled:opacity-50 w-full sm:w-auto"
                      >
                        <Printer className="h-3.5 w-3.5" />
                        {isFaxSubmitting ? 'Sending...' : 'Send Fax'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {showSendButtons && isEmailChannel && (
                  <Tooltip
                    content={
                      !isHipaaPackageReady
                        ? 'Complete the HIPAA package first'
                        : isEmailMissing
                          ? 'Add an email address first'
                          : isFirmChannelMissing
                            ? 'No firm channel configured — will send from records'
                            : 'Send HIPAA package via email'
                    }
                  >
                    <span>
                      <Button
                        size="sm"
                        onClick={handleSendEmail}
                        disabled={!canSendEmail || isEmailSubmitting}
                        className="h-7 text-xs gap-1 bg-zinc-900 hover:bg-zinc-800 text-white disabled:opacity-50 w-full sm:w-auto"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        {isEmailSubmitting ? 'Sending...' : 'Send Email'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {showSendButtons && isMailChannel && (
                  <Button
                    size="sm"
                    onClick={handleSendMail}
                    className="h-7 text-xs gap-1 bg-zinc-900 hover:bg-zinc-800 text-white w-full sm:w-auto"
                  >
                    <Send className="h-3.5 w-3.5" />
                    Send Mail
                  </Button>
                )}

                {/* Prepare HIPAA button when package not ready */}
                {showPrepareHipaa && (
                  <Button
                    size="sm"
                    onClick={handleOpenPackage}
                    className="h-7 text-xs bg-zinc-900 hover:bg-zinc-800 text-white w-full sm:w-auto"
                  >
                    Prepare HIPAA
                  </Button>
                )}

                {/* Default CTA for non-send states (not pending with fax/email/mail) */}
                {!showSendButtons && !showPrepareHipaa && (
                  <Button
                    size="sm"
                    onClick={handleCtaClick}
                    disabled={isChangingStatus}
                    className="h-7 text-xs bg-zinc-900 hover:bg-zinc-800 text-white w-full sm:w-auto"
                  >
                    {isChangingStatus ? 'Updating...' : ctaText}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Transmission Status - for fax and email channels (shown first) */}
        <CompactTransmissionStatus
          channelType={request.channel?.type}
          faxStatus={faxStatus}
          emailStatus={emailStatus}
          isEmailLoading={isEmailStatusLoading}
          onRefreshFax={handleRefreshFax}
          isFaxRefreshing={isRefreshingFaxStatus || isFaxLoading}
        />

        {/* Voice AI Section - show if Voice AI data exists and no follow-up alternates */}
        {!hasFollowUpAlternates && hasVoiceAIData && extendedMetadata && (
          <CompactVoiceAIInfo metadata={extendedMetadata} />
        )}

        {/* Follow-up Alternate Contacts */}
        <CompactAlternates
          alternates={alternates}
          isLoading={isAlternatesLoading}
          onAccept={resendWithAlternate}
          isAnyResendInFlight={isAnyResendInFlight}
          activeAlternateKey={activeAlternateKey}
          currentChannel={request.channel}
        />

        {/* Follow-ups Disclosure */}
        <CompactFollowUps
          matterId={matterId}
          providerId={provider.id}
          request={request}
          frontConversationUrl={request.metadata?.front_conversation_url}
        />
      </div>

      {/* Select Destination Modal */}
      <SelectDestinationModal
        isOpen={isChannelEditorOpen}
        onClose={() => setIsChannelEditorOpen(false)}
        onSuccess={() => {
          // Invalidate the request cache to refresh the card
          dispatch(
            treatmentRecordRequestService.util.invalidateTags([
              { type: 'record-request', id: request.id },
              { type: 'record-requests', id: provider.id },
            ]),
          );
        }}
        request={request}
        providerId={provider.id}
        matterId={matterId}
        provider={provider}
      />

      <ResendRequestModal
        open={isResendModalOpen}
        onClose={() => setIsResendModalOpen(false)}
        matterId={matterId}
        providerId={provider.id}
        requestId={request.id}
        providerServiceId={provider.provider_service_ref_id}
        recordType={request.provided_records?.[0]}
        currentScope={request.provided_records?.join(', ')}
        currentChannelType={request.channel?.type}
        currentChannelValue={
          request.channel?.fax ??
          request.channel?.email ??
          request.channel?.phone_number ??
          request.channel?.address ??
          request.channel?.instructions ??
          undefined
        }
      />

      {/* Fax Submission Modal */}
      <CatalystDialog
        open={isFaxModalOpen}
        onClose={() => !isFaxSubmitting && setIsFaxModalOpen(false)}
        size="lg"
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      >
        <DialogBody>
          <CatalystDialogTitle>Send HIPAA Package via Fax</CatalystDialogTitle>
          <DialogDescription>
            Optionally add cover page details before sending to{' '}
            {request.channel?.fax || 'the provider'}.
          </DialogDescription>
          <div className="mt-4 flex flex-col gap-3">
            <Input
              type="text"
              placeholder="Cover page To (optional)"
              value={faxCoverTo}
              onChange={(e) => setFaxCoverTo(e.target.value)}
            />
            <Input
              type="text"
              placeholder="Cover page From (optional)"
              value={faxCoverFrom}
              onChange={(e) => setFaxCoverFrom(e.target.value)}
            />
            <Input
              type="text"
              placeholder="Cover page Message (optional)"
              value={faxCoverMessage}
              onChange={(e) => setFaxCoverMessage(e.target.value)}
            />
          </div>
        </DialogBody>
        <DialogActions>
          <CatalystButton
            outline
            onClick={() => setIsFaxModalOpen(false)}
            disabled={isFaxSubmitting}
          >
            Cancel
          </CatalystButton>
          <CatalystButton
            color="indigo-steel"
            onClick={handleConfirmFaxSubmit}
            disabled={isFaxSubmitting}
          >
            {isFaxSubmitting ? 'Sending...' : 'Send Fax'}
          </CatalystButton>
        </DialogActions>
      </CatalystDialog>

      {/* Email Submission Modal */}
      <CatalystDialog
        open={isEmailModalOpen}
        onClose={() => !isEmailSubmitting && setIsEmailModalOpen(false)}
        size="lg"
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      >
        <DialogBody>
          <CatalystDialogTitle>
            Send HIPAA Package via Email
          </CatalystDialogTitle>
          <DialogDescription>
            Review and customize the email before sending to{' '}
            {request.channel?.email || 'the provider'}.
          </DialogDescription>
          <div className="mt-4 space-y-4">
            {isEmailTemplateLoading || isEmailTemplateFetching ? (
              <div className="flex items-center justify-center py-10 text-sm text-gray-600">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                  <span>Loading email template...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-zinc-900">
                    To
                  </Label>
                  <Input
                    value={request.channel?.email || 'No email configured'}
                    disabled
                    className="bg-gray-50 text-gray-700"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-zinc-900">
                    From
                  </Label>
                  <Select
                    value={emailSenderOption}
                    onValueChange={(value) =>
                      setEmailSenderOption(value as 'firm' | 'records')
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select sender" />
                    </SelectTrigger>
                    <SelectContent
                      className="border border-gray-200 bg-white"
                      style={{ zIndex: Z_INDEX_DROPDOWN_IN_MODAL }}
                    >
                      <SelectItem
                        value="firm"
                        disabled={!firmFrontChannel?.address}
                        className={!firmFrontChannel?.address ? 'opacity-50' : ''}
                      >
                        {firmFrontChannel?.address
                          ? `Firm (${firmFrontChannel.address})`
                          : 'Firm Front channel (not configured)'}
                      </SelectItem>
                      <SelectItem
                        value="records"
                        disabled={!RECORDS_FRONT_CHANNEL_ID.trim()}
                        className={!RECORDS_FRONT_CHANNEL_ID.trim() ? 'opacity-50' : ''}
                      >
                        {RECORDS_SENDER_ADDRESS}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {emailSenderOption === 'firm' &&
                    !firmFrontChannel?.address && (
                      <div className="text-xs text-red-600">
                        Configure firm Front channel in Firms to send emails
                      </div>
                    )}
                  {emailSenderOption === 'records' &&
                    !RECORDS_FRONT_CHANNEL_ID.trim() && (
                      <div className="text-xs text-red-600">
                        Records sender channel ID not configured
                      </div>
                    )}
                  {isFirmChannelMissing && emailSenderOption === 'records' && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                      No firm-specific Front channel is configured. Emails will
                      be sent from the shared records address. Consider
                      configuring a firm-specific channel in Firm settings for a
                      more personalized sender.
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-zinc-900">
                    Subject
                  </Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => {
                      setEmailSubject(e.target.value);
                      if (emailValidationError) setEmailValidationError(null);
                    }}
                    placeholder="Email subject"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-zinc-900">
                    Body
                  </Label>
                  <Textarea
                    value={emailBody}
                    onChange={(e) => {
                      setEmailBody(e.target.value);
                      if (emailValidationError) setEmailValidationError(null);
                    }}
                    rows={8}
                    placeholder="Email body"
                  />
                </div>
                {emailValidationError && (
                  <div className="text-xs text-red-600">
                    {emailValidationError}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <CatalystButton
            outline
            onClick={() => setIsEmailModalOpen(false)}
            disabled={isEmailSubmitting}
          >
            Cancel
          </CatalystButton>
          <CatalystButton
            color="indigo-steel"
            onClick={handleConfirmEmailSubmit}
            disabled={
              isEmailSubmitting ||
              isEmailTemplateLoading ||
              isEmailTemplateFetching
            }
          >
            {isEmailSubmitting ? 'Sending...' : 'Send Email'}
          </CatalystButton>
        </DialogActions>
      </CatalystDialog>

      {/* Mail Coming Soon Modal */}
      <CatalystDialog
        open={isMailComingSoonOpen}
        onClose={() => setIsMailComingSoonOpen(false)}
        size="sm"
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      >
        <DialogBody>
          <CatalystDialogTitle>Mail Submission Coming Soon</CatalystDialogTitle>
          <DialogDescription>
            Direct mail submission from this view is coming soon. For now,
            please use the <strong>View Package</strong> button to access the
            full mail submission workflow with mailing class and cheque options.
          </DialogDescription>
        </DialogBody>
        <DialogActions>
          <CatalystButton
            outline
            onClick={() => setIsMailComingSoonOpen(false)}
          >
            Got it
          </CatalystButton>
          <CatalystButton
            color="indigo-steel"
            onClick={() => {
              setIsMailComingSoonOpen(false);
              handleOpenPackage();
            }}
          >
            View Package
          </CatalystButton>
        </DialogActions>
      </CatalystDialog>

      {/* Invoice Upload Modal */}
      <CatalystDialog
        open={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        size="lg"
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      >
        <CatalystDialogTitle>Submit Invoice</CatalystDialogTitle>
        <DialogDescription>
          Upload the invoice document and enter invoice details for this records
          request.
        </DialogDescription>
        <DialogBody>
          <div className="space-y-4">
            <div>
              <Label htmlFor="invoice-amount">Invoice Amount ($)</Label>
              <Input
                id="invoice-amount"
                type="number"
                step="0.01"
                value={invoiceAmount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setInvoiceAmount(e.target.value)
                }
                placeholder="0.00"
                disabled={isSubmittingInvoice}
              />
            </div>

            <div>
              <Label>Invoice Document</Label>
              <div className="mt-2">
                {invoiceSelectedBlobIds.length > 0 ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <span className="text-sm text-gray-700">
                      {invoiceSelectedBlobIds.length} document
                      {invoiceSelectedBlobIds.length !== 1 ? 's' : ''} selected
                    </span>
                    <CatalystButton
                      outline
                      onClick={() => setIsInvoiceDocModalOpen(true)}
                      disabled={isSubmittingInvoice}
                    >
                      Change
                    </CatalystButton>
                  </div>
                ) : (
                  <CatalystButton
                    outline
                    onClick={() => setIsInvoiceDocModalOpen(true)}
                    disabled={isSubmittingInvoice}
                    className="w-full"
                  >
                    Select or Upload Invoice
                  </CatalystButton>
                )}
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <CatalystButton
            outline
            onClick={() => setIsInvoiceModalOpen(false)}
            disabled={isSubmittingInvoice}
          >
            Cancel
          </CatalystButton>
          <CatalystButton
            color="zinc"
            onClick={handleSkipInvoice}
            disabled={isSubmittingInvoice || isChangingStatus}
          >
            Skip & Continue
          </CatalystButton>
          <CatalystButton
            color="indigo-steel"
            onClick={handleSubmitInvoice}
            disabled={
              isSubmittingInvoice ||
              !invoiceSelectedBlobIds.length ||
              !invoiceAmount
            }
          >
            {isSubmittingInvoice
              ? 'Submitting...'
              : 'Submit Invoice & Continue'}
          </CatalystButton>
        </DialogActions>
      </CatalystDialog>

      {/* Invoice Document Selection Modal */}
      {isInvoiceDocModalOpen && (
        <DocumentSelectionModal
          open={isInvoiceDocModalOpen}
          onClose={() => setIsInvoiceDocModalOpen(false)}
          matterId={matterId}
          onSelectDocuments={async (blobIds: string[]) => {
            setInvoiceSelectedBlobIds(blobIds);
            setIsInvoiceDocModalOpen(false);
          }}
          initialSelectedBlobIds={invoiceSelectedBlobIds}
          singleFile
          onUpload={handleUploadToCollection}
        />
      )}

      {/* Records Upload Modal - directly shows document selector */}
      {isRecordsModalOpen && (
        <DocumentSelectionModal
          open={isRecordsModalOpen}
          onClose={() => setIsRecordsModalOpen(false)}
          matterId={matterId}
          onSelectDocuments={async (blobIds: string[]) => {
            if (blobIds.length === 0) {
              toast.error(
                'Please select at least one file to complete this request.',
              );
              return;
            }
            await handleStatusChange('records_ingested', blobIds);
          }}
          initialSelectedBlobIds={[]}
          onUpload={handleUploadToCollection}
          description="Upload Record Set: To mark this request as complete, please upload or select the medical record files."
          requireSelection
          onBypass={handleRecordsBypass}
        />
      )}
    </div>
  );
};

// Requests section within provider card
export interface RequestsSectionProps {
  requests: TreatmentRecordRequestSchema[] | undefined;
  provider: MedicalProvider;
  matterId: string;
  onNewRequest: () => void;
}

export const RequestsSection = ({
  requests,
  provider,
  matterId,
  onNewRequest,
}: RequestsSectionProps) => {
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(
    null,
  );
  const didAutoExpandFirstRequestRef = useRef(false);

  // Helper to check if a request is superseded (has a superseded_by_request_id)
  const isRequestSuperseded = (
    request: TreatmentRecordRequestSchema,
  ): boolean => {
    const metadata = request.metadata as Record<string, unknown> | undefined;
    const supersededById = metadata?.superseded_by_request_id;
    return Boolean(
      supersededById &&
        typeof supersededById === 'string' &&
        supersededById !== '',
    );
  };

  // Helper to check if a request has a terminal status that should be collapsed by default
  const isRequestTerminal = (
    request: TreatmentRecordRequestSchema,
  ): boolean => {
    const terminalStatuses = [
      'cancelled',
      'rejected',
      'auto_retried',
      'records_ingested',
    ];
    return terminalStatuses.includes(request.status ?? '');
  };

  // Show all requests (don't filter superseded ones)
  const visibleRequests = useMemo(() => requests ?? [], [requests]);

  const inferredTemplateType = useMemo(() => {
    const autoTemplateRequests = visibleRequests.filter((r) => {
      const metadata = r.metadata as
        | {
            prp_plan_id?: unknown;
            prp_request_group_key?: unknown;
          }
        | undefined;

      return Boolean(
        metadata?.prp_request_group_key &&
          !metadata?.prp_plan_id &&
          typeof metadata?.prp_request_group_key === 'string',
      );
    });

    if (autoTemplateRequests.length === 0) return null;

    const hasHospitalSignals = autoTemplateRequests.some((r) =>
      (r.provided_records ?? []).some(
        (scope) => scope === 'Radiology' || scope.startsWith('ER '),
      ),
    );

    return hasHospitalSignals || autoTemplateRequests.length >= 3
      ? 'hospital'
      : 'default';
  }, [visibleRequests]);

  // Find the first non-superseded, non-terminal request for auto-expansion
  const firstActiveRequest = useMemo(() => {
    return visibleRequests.find(
      (r) => !isRequestSuperseded(r) && !isRequestTerminal(r),
    );
  }, [visibleRequests]);

  useEffect(() => {
    if (didAutoExpandFirstRequestRef.current) {
      return;
    }
    if (!firstActiveRequest) {
      return;
    }

    setExpandedRequestId(firstActiveRequest.id);
    didAutoExpandFirstRequestRef.current = true;
  }, [firstActiveRequest]);

  const toggleExpand = (requestId: string) => {
    setExpandedRequestId((prev) => (prev === requestId ? null : requestId));
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-2 gap-2">
        <span className="text-[10px] font-semibold text-zinc-600 uppercase">
          Record Requests
        </span>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onNewRequest}
            className="flex items-center gap-1 text-[10px] font-medium text-zinc-600 hover:text-zinc-800 px-1.5 py-0.5 hover:bg-zinc-100 rounded transition-colors"
          >
            <Plus className="h-3 w-3" />
            New Request
          </button>
        </div>
      </div>

      {inferredTemplateType && (
        <div className="mb-2 p-2 rounded-lg border border-amber-200 bg-amber-50">
          <div className="text-[11px] text-amber-900">
            {inferredTemplateType === 'hospital'
              ? 'Hospitals often require multiple departments. We created the standard hospital request set.'
              : 'We auto-created the standard request set for completeness.'}
          </div>
        </div>
      )}

      {/* Request list */}
      {visibleRequests.length > 0 ? (
        <div className="flex flex-col gap-1">
          {visibleRequests.map((request) => {
            const isExpanded = expandedRequestId === request.id;
            const isSuperseded = isRequestSuperseded(request);
            // All requests can be expanded (superseded and terminal are just auto-collapsed)
            return (
              <div key={request.id}>
                {isExpanded ? (
                  <RequestDetail
                    request={request}
                    provider={provider}
                    onCollapse={() => setExpandedRequestId(null)}
                    onDelete={() => setExpandedRequestId(null)}
                  />
                ) : (
                  <RequestRow
                    request={request}
                    isExpanded={false}
                    onToggle={() => toggleExpand(request.id)}
                    isSuperseded={isSuperseded}
                    matterId={matterId}
                    providerId={provider.id}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 border border-dashed border-zinc-300 rounded-lg">
          <span className="text-xs text-zinc-500 mb-2">No requests yet</span>
          <button
            type="button"
            onClick={onNewRequest}
            className="flex items-center gap-1 text-xs font-medium text-zinc-700 hover:text-zinc-900 px-2 py-1 bg-zinc-100 hover:bg-zinc-200 rounded transition-colors"
          >
            <Plus className="h-3 w-3" />
            Create Request
          </button>
        </div>
      )}
    </div>
  );
};

// Compact contact item with actions
interface ContactItemProps {
  handle: ContactHandle;
  type: 'phone' | 'email' | 'fax';
  preferred?: boolean;
  onCall?: (phone: string) => void;
  onEmail?: (email: string) => void;
}

const ContactItem = ({
  handle,
  type,
  preferred,
  onCall,
  onEmail,
}: ContactItemProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(handle.value);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === 'phone' && onCall) {
      onCall(handle.value);
    } else if (type === 'email' && onEmail) {
      onEmail(handle.value);
    }
  };

  const getIcon = () => {
    if (type === 'phone') return <Phone className="h-3 w-3" />;
    if (type === 'email') return <Mail className="h-3 w-3" />;
    return <Printer className="h-3 w-3" />;
  };

  const getActionLabel = () => {
    if (type === 'phone') return 'Call';
    if (type === 'email') return 'Email';
    return null;
  };

  const icon = getIcon();
  const actionLabel = getActionLabel();

  return (
    <div className="group flex items-center gap-1.5">
      <span className="text-zinc-500">{icon}</span>
      {preferred && (
        <Tooltip
          content="Preferred contact for records"
          variant="dark"
          side="top"
        >
          <Star className="h-3 w-3 text-amber-500 fill-amber-200" />
        </Tooltip>
      )}
      <Tooltip content="Click to copy" variant="dark" side="top">
        <button
          type="button"
          onClick={handleCopy}
          className={`text-xs font-medium transition-colors ${
            copied
              ? 'text-green-600'
              : 'text-zinc-800 hover:text-blue-600 hover:underline'
          }`}
        >
          {handle.value}
        </button>
      </Tooltip>
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <button
          type="button"
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-zinc-700"
        >
          <Copy className="h-3 w-3" />
        </button>
      )}
      {actionLabel && (onCall || onEmail) && (
        <button
          type="button"
          onClick={handleAction}
          className="opacity-0 group-hover:opacity-100 ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-zinc-200 hover:bg-zinc-300 text-zinc-700 rounded transition-all"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};

// Contact section for the modal
interface ContactSectionProps {
  title: string;
  items: ContactHandle[];
  type: 'phone' | 'email' | 'fax' | 'other';
  onCall: (phone: string) => void;
  onEmail: (email: string) => void;
}

const ContactSection = ({
  title,
  items,
  type,
  onCall,
  onEmail,
}: ContactSectionProps) => {
  if (items.length === 0) return null;

  const isUrl = (value: string) => {
    return (
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('www.')
    );
  };

  const formatUrl = (value: string) => {
    if (value.startsWith('www.')) return `https://${value}`;
    return value;
  };

  return (
    <div className="space-y-1">
      <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">
        {title}
      </h4>
      <div className="space-y-1">
        {items.map((handle, idx) => (
          <div
            key={`${type}-${idx}`}
            className="flex items-center justify-between gap-2 py-1 px-2 bg-zinc-50 rounded text-xs"
          >
            <div className="flex flex-col min-w-0 flex-1">
              {isUrl(handle.value) ? (
                <a
                  href={formatUrl(handle.value)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline truncate"
                  title={handle.value}
                >
                  {handle.value}
                </a>
              ) : (
                <span
                  className="text-xs font-medium text-zinc-900 truncate"
                  title={handle.value}
                >
                  {handle.value}
                </span>
              )}
              {handle.name && (
                <span className="text-[10px] text-zinc-500 truncate">
                  {handle.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {type === 'phone' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCall(handle.value)}
                  className="h-6 px-2 text-[10px]"
                >
                  <Phone className="h-3 w-3 mr-1" />
                  Call
                </Button>
              )}
              {type === 'email' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEmail(handle.value)}
                  className="h-6 px-2 text-[10px]"
                >
                  <Mail className="h-3 w-3 mr-1" />
                  Email
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// All contacts modal
interface AllContactsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerName: string;
  handles: Array<ContactHandle & { isFromDirectory?: boolean }>;
  onCall: (phone: string) => void;
  onEmail: (email: string) => void;
}

const AllContactsModal = ({
  open,
  onOpenChange,
  providerName,
  handles,
  onCall,
  onEmail,
}: AllContactsModalProps) => {
  const phoneHandles = handles.filter(
    (h) => h.handle_type === ContactHandleType.PHONE,
  );
  const emailHandles = handles.filter(
    (h) => h.handle_type === ContactHandleType.EMAIL,
  );
  const faxHandles = handles.filter(
    (h) => h.handle_type === ContactHandleType.FAX,
  );
  const otherHandles = handles.filter(
    (h) =>
      h.handle_type !== ContactHandleType.PHONE &&
      h.handle_type !== ContactHandleType.EMAIL &&
      h.handle_type !== ContactHandleType.FAX,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Contacts for {providerName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1 max-h-[60vh] overflow-y-auto">
          <ContactSection
            title="Phone"
            items={phoneHandles}
            type="phone"
            onCall={onCall}
            onEmail={onEmail}
          />
          <ContactSection
            title="Email"
            items={emailHandles}
            type="email"
            onCall={onCall}
            onEmail={onEmail}
          />
          <ContactSection
            title="Fax"
            items={faxHandles}
            type="fax"
            onCall={onCall}
            onEmail={onEmail}
          />
          <ContactSection
            title="Other"
            items={otherHandles}
            type="other"
            onCall={onCall}
            onEmail={onEmail}
          />
          {handles.length === 0 && (
            <p className="text-sm text-zinc-600 text-center py-4">
              No contact information available
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Provider card component
export const ProviderCard = ({ provider }: { provider: MedicalProvider }) => {
  const navigate = useNavigate();
  const { matterId } = useMatter();
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isContactsModalOpen, setIsContactsModalOpen] = useState(false);
  const [isCreateRequestModalOpen, setIsCreateRequestModalOpen] =
    useState(false);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [selectedEmail, setSelectedEmail] = useState('');

  // Fetch requests for this provider
  const { data: requests } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsListQuery({
      matterId,
      providerId: provider.id,
    });

  // Fetch directory contacts
  const { handlesByUnit } = useProviderDirectory({
    providerRefId: provider.provider_contact_ref_id,
    skip: !provider.provider_contact_ref_id,
  });

  // Merge handles from provider and directory
  const allHandles = useMemo<
    Array<ContactHandle & { isFromDirectory?: boolean }>
  >(() => {
    const handles: Array<ContactHandle & { isFromDirectory?: boolean }> = [
      ...(provider.handles || []).map((h) => ({
        ...h,
        isFromDirectory: false,
      })),
    ];

    if (provider.provider_contact_ref_id && handlesByUnit) {
      Object.values(handlesByUnit).forEach((unitHandles) => {
        unitHandles.forEach((dirHandle) => {
          handles.push({
            value: dirHandle.handle.value,
            handle_type: dirHandle.handle.handle_type as ContactHandleType,
            name: dirHandle.handle.name || undefined,
            isFromDirectory: true,
          });
        });
      });
    }

    return handles;
  }, [provider.handles, handlesByUnit, provider.provider_contact_ref_id]);

  // Get primary contacts (first of each type)
  const primaryPhone = allHandles.find(
    (h) => h.handle_type === ContactHandleType.PHONE,
  );
  const primaryEmail = allHandles.find(
    (h) => h.handle_type === ContactHandleType.EMAIL,
  );
  const primaryFax = allHandles.find(
    (h) => h.handle_type === ContactHandleType.FAX,
  );

  // Count additional contacts
  const additionalCount =
    allHandles.length -
    [primaryPhone, primaryEmail, primaryFax].filter(Boolean).length;

  const { startingDate, endingDate } = useMemo(
    () => getProviderTreatmentDates(provider),
    [provider],
  );

  const treatmentPeriod = formatProviderTreatmentDates(
    provider.treatment_status,
    startingDate,
    endingDate,
  );

  const visitCount = provider.encounters?.length ?? 0;

  const physicianName = useMemo(() => {
    if (provider.practitioner_names && provider.practitioner_names.length > 0) {
      return provider.practitioner_names[0];
    }
    return null;
  }, [provider.practitioner_names]);

  const specialty = useMemo(() => {
    if (provider.mentions && provider.mentions.length > 0) {
      const mentionWithSpecialty = provider.mentions.find(
        (mention) => mention.specialty_hint,
      );
      if (mentionWithSpecialty) {
        return mentionWithSpecialty.specialty_hint;
      }
    }
    return null;
  }, [provider.mentions]);

  // Get enrichment data from mentions for records guidance
  const enrichment = useMemo(
    () => getEnrichmentFromMentions(provider.mentions),
    [provider.mentions],
  );

  // Check if any request for this provider has Voice AI data (hides enrichment if true)
  const anyRequestHasVoiceAI = useMemo(() => {
    return requests?.some((r) => hasVoiceAIContactData(r.metadata)) ?? false;
  }, [requests]);

  // Create a set of preferred handle values for quick lookup
  const preferredHandleValues = useMemo(() => {
    if (!enrichment?.handles) return new Set<string>();
    return new Set(
      enrichment.handles
        .filter((h) => h.preferred && h.value)
        .map((h) => h.value!),
    );
  }, [enrichment]);

  const taskCount = provider.tasks?.length ?? 0;

  const handleProviderClick = () => {
    navigate(`/matters/${matterId}/treatment/${provider.id}`);
  };

  const handleCall = (phone: string) => {
    setSelectedPhone(phone);
    setIsCallModalOpen(true);
  };

  const handleEmail = (email: string) => {
    setSelectedEmail(email);
    setIsEmailDialogOpen(true);
  };

  const handleNewRequest = () => {
    setIsCreateRequestModalOpen(true);
  };

  return (
    <>
      <div className="border border-zinc-300 rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-100/50 rounded-t-lg">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-zinc-900">
              {provider.name || 'Unnamed Provider'}
            </h3>
            <TreatmentStatusDropdown provider={provider} />
            <CareLevelDropdown provider={provider} />
            <IdentityStatusBadge
              status={provider.identity_status}
              matterId={matterId}
              providerId={provider.id}
              identityDueAt={provider.identity_due_at}
              identityResolvedAt={provider.identity_resolved_at}
              identityDeferReason={provider.identity_defer_reason}
            />
            {provider.procedure_types &&
              provider.procedure_types.length > 0 && (
                <>
                  {provider.procedure_types
                    .filter(
                      (pt) =>
                        pt !== ProcedureType.UNKNOWN && procedureTypeLabel[pt],
                    )
                    .map((pt) => (
                      <Badge
                        key={pt}
                        color="zinc"
                        className="text-[10px] px-1.5 py-0.5"
                      >
                        {procedureTypeLabel[pt]}
                      </Badge>
                    ))}
                </>
              )}
          </div>
          <div className="flex items-center gap-1.5">
            {taskCount > 0 && (
              <Badge color="violet" className="text-[10px] px-1.5 py-0.5">
                {taskCount} Tasks
              </Badge>
            )}
            <button
              type="button"
              onClick={handleProviderClick}
              className="p-1 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-200 rounded transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-3">
          <div className="grid grid-cols-[1fr_1fr] gap-6">
            {/* Left column - Provider details */}
            <div className="space-y-1.5">
              <div className="flex gap-2">
                <span className="text-xs text-zinc-600 w-24 flex-shrink-0">
                  Treatment
                </span>
                <span className="text-xs font-medium text-zinc-900 truncate">
                  {provider.treatment || '—'}
                </span>
              </div>
              <div className="flex gap-2">
                <span className="text-xs text-zinc-600 w-24 flex-shrink-0">
                  Period
                </span>
                <span className="text-xs font-medium text-zinc-900">
                  {treatmentPeriod !== 'Unknown'
                    ? `${treatmentPeriod}${visitCount > 0 ? ` (${visitCount} visits)` : ''}`
                    : '—'}
                </span>
              </div>
              <div className="flex gap-2">
                <span className="text-xs text-zinc-600 w-24 flex-shrink-0">
                  Physician
                </span>
                <span className="text-xs font-medium text-zinc-900">
                  {physicianName || '—'}
                </span>
              </div>
              <div className="flex gap-2">
                <span className="text-xs text-zinc-600 w-24 flex-shrink-0">
                  Specialty
                </span>
                <span className="text-xs font-medium text-zinc-900">
                  {specialty || '—'}
                </span>
              </div>

              {/* Contact Info */}
              <div className="pt-2 mt-2 border-t border-zinc-200 space-y-1">
                {primaryPhone && (
                  <ContactItem
                    handle={primaryPhone}
                    type="phone"
                    preferred={preferredHandleValues.has(primaryPhone.value)}
                    onCall={handleCall}
                  />
                )}
                {primaryEmail && (
                  <ContactItem
                    handle={primaryEmail}
                    type="email"
                    preferred={preferredHandleValues.has(primaryEmail.value)}
                    onEmail={handleEmail}
                  />
                )}
                {primaryFax && (
                  <ContactItem
                    handle={primaryFax}
                    type="fax"
                    preferred={preferredHandleValues.has(primaryFax.value)}
                  />
                )}
                {provider.provider_service?.portal_eligible &&
                  provider.provider_service.portal_type &&
                  provider.provider_service.portal_type.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-purple-600">
                        <ExternalLink className="h-3 w-3" />
                      </span>
                      <span className="text-xs font-medium text-purple-700">
                        {provider.provider_service.portal_type
                          .map((pt) =>
                            pt === 'chartswap'
                              ? 'ChartSwap'
                              : pt === 'smart_request'
                                ? 'SmartRequest'
                                : pt === 'mro'
                                  ? 'MRO'
                                  : pt,
                          )
                          .join(' · ')}
                      </span>
                    </div>
                  )}
                {!primaryPhone &&
                  !primaryEmail &&
                  !primaryFax &&
                  !(
                    provider.provider_service?.portal_eligible &&
                    provider.provider_service.portal_type &&
                    provider.provider_service.portal_type.length > 0
                  ) && (
                    <span className="text-xs text-zinc-500 italic">
                      No contact info
                    </span>
                  )}
                {additionalCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsContactsModalOpen(true)}
                    className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-800 mt-1"
                  >
                    <MoreHorizontal className="h-3 w-3" />
                    {additionalCount} more contact
                    {additionalCount > 1 ? 's' : ''}
                  </button>
                )}

                {/* Records Request Guidance - hidden when Voice AI data exists */}
                {enrichment && !anyRequestHasVoiceAI && (
                  <EnrichmentRecordsGuidance
                    enrichment={enrichment}
                    providerId={provider.id}
                  />
                )}
              </div>
            </div>

            {/* Right column - Requests */}
            <RequestsSection
              requests={requests}
              provider={provider}
              matterId={matterId}
              onNewRequest={handleNewRequest}
            />
          </div>
        </div>
      </div>

      {/* Call Modal */}
      <MakeCallModal
        isOpen={isCallModalOpen}
        onClose={() => setIsCallModalOpen(false)}
        initialPhoneNumber={selectedPhone}
        initialContactLabel={provider.name || 'Provider'}
        matterId={matterId}
        source="treatment_provider"
      />

      {/* Email Dialog */}
      <SendFrontEmailDialog
        open={isEmailDialogOpen}
        onClose={() => setIsEmailDialogOpen(false)}
        matterId={matterId}
        defaultTo={selectedEmail ? [selectedEmail] : []}
        defaultSubject=""
      />

      {/* All Contacts Modal */}
      <AllContactsModal
        open={isContactsModalOpen}
        onOpenChange={setIsContactsModalOpen}
        providerName={provider.name || 'Provider'}
        handles={allHandles}
        onCall={handleCall}
        onEmail={handleEmail}
      />

      {/* Create Request Modal */}
      <CreateProviderRequestDialog
        isOpen={isCreateRequestModalOpen}
        setIsOpen={setIsCreateRequestModalOpen}
        provider={provider}
      />
    </>
  );
};

// Empty state component
const EmptyProvidersState = ({ pendingCount }: { pendingCount: number }) => {
  const message =
    pendingCount > 0
      ? `${pendingCount} provider${pendingCount === 1 ? '' : 's'} awaiting review`
      : 'Providers will appear here once approved';

  return (
    <div className="border border-dashed border-zinc-400 rounded-lg py-10 text-center">
      <p className="text-sm text-zinc-600">No approved providers yet</p>
      <p className="text-xs text-zinc-500 mt-1">{message}</p>
    </div>
  );
};

// Skeleton for loading state
const ProviderCardSkeleton = () => (
  <div className="border border-zinc-300 rounded-lg bg-white animate-pulse">
    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-100/50 rounded-t-lg">
      <div className="flex items-center gap-2">
        <div className="h-4 bg-zinc-300 rounded w-32" />
        <div className="h-5 bg-zinc-300 rounded w-16" />
      </div>
      <div className="h-4 bg-zinc-300 rounded w-4" />
    </div>
    <div className="px-4 py-3">
      <div className="space-y-2">
        <div className="h-3 bg-zinc-200 rounded w-3/4" />
        <div className="h-3 bg-zinc-200 rounded w-2/3" />
        <div className="h-3 bg-zinc-200 rounded w-1/2" />
      </div>
    </div>
  </div>
);

interface TreatmentProvidersSectionProps {
  providers: MedicalProvider[];
  isLoading: boolean;
}

export const TreatmentProvidersSection = ({
  providers,
  isLoading,
}: TreatmentProvidersSectionProps) => {
  const { matterId } = useMatter();
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isAddProviderOpen, setIsAddProviderOpen] = useState(false);
  const { data: incidents } = useGetIncidentsQuery(
    { matterId },
    { skip: !isAddProviderOpen },
  );

  const incidentCenter = useMemo(() => {
    const list = incidents ?? [];
    const best =
      list.find(
        (i) =>
          i.normalized_location?.latitude != null &&
          i.normalized_location?.longitude != null,
      ) ??
      list.find(
        (i) => i.extracted_latitude != null && i.extracted_longitude != null,
      );
    if (!best) return null;
    const lat = best.normalized_location?.latitude ?? best.extracted_latitude;
    const lng = best.normalized_location?.longitude ?? best.extracted_longitude;
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [incidents]);

  const approvedProviders = useMemo(() => {
    if (!providers) return [];
    return providers.filter((p) => p.status === ProviderStatus.APPROVED);
  }, [providers]);

  const approvedProvidersByCareLevel = useMemo(() => {
    const groups: Record<string, MedicalProvider[]> = {};
    approvedProviders.forEach((p) => {
      const key = p.care_level ?? 'UNCATEGORIZED';
      groups[key] = groups[key] ?? [];
      groups[key].push(p);
    });
    return groups;
  }, [approvedProviders]);

  const pendingCount = useMemo(() => {
    if (!providers) return 0;
    return providers.filter(
      (p) => !p.status || p.status === ProviderStatus.NEEDS_APPROVAL,
    ).length;
  }, [providers]);

  const careLevelOrder: Array<TreatmentCareLevel | 'UNCATEGORIZED'> = [
    TreatmentCareLevel.EMERGENCY_CARE,
    TreatmentCareLevel.CONSERVATIVE_CARE,
    TreatmentCareLevel.DIAGNOSTIC_TESTING,
    TreatmentCareLevel.SPECIALIST_CARE,
    TreatmentCareLevel.SURGICAL_CARE,
    'UNCATEGORIZED',
  ];

  const careLevelLabel: Record<string, string> = {
    [TreatmentCareLevel.EMERGENCY_CARE]: 'Emergency Care',
    [TreatmentCareLevel.CONSERVATIVE_CARE]: 'Conservative Care',
    [TreatmentCareLevel.DIAGNOSTIC_TESTING]: 'Diagnostic Testing',
    [TreatmentCareLevel.SPECIALIST_CARE]: 'Specialist Care',
    [TreatmentCareLevel.SURGICAL_CARE]: 'Surgical Care',
    UNCATEGORIZED: 'Uncategorized',
  };

  return (
    <>
      <style>{recordRequestProgressAnimatedStyles}</style>
      <div className="flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">
            Treatment Providers
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsManageModalOpen(true)}
              className="h-7 text-xs relative"
            >
              Review Providers
              {pendingCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-500 text-white">
                  {pendingCount}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddProviderOpen(true)}
              className="h-7 text-xs"
            >
              Add Provider
            </Button>
          </div>
        </div>

        {/* Provider Cards */}
        <div className="flex flex-col gap-2">
          {isLoading && (
            <>
              <ProviderCardSkeleton />
              <ProviderCardSkeleton />
            </>
          )}
          {!isLoading && approvedProviders.length === 0 && (
            <EmptyProvidersState pendingCount={pendingCount} />
          )}
          {!isLoading &&
            approvedProviders.length > 0 &&
            careLevelOrder
              .filter(
                (lvl) => (approvedProvidersByCareLevel[lvl] ?? []).length > 0,
              )
              .map((lvl) => (
                <div key={lvl} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between pt-2">
                    <div className="text-xs font-medium text-zinc-600">
                      {careLevelLabel[lvl]}{' '}
                      <span className="text-zinc-400">
                        ({approvedProvidersByCareLevel[lvl].length})
                      </span>
                    </div>
                  </div>
                  {approvedProvidersByCareLevel[lvl].map((provider) => (
                    <ProviderCard key={provider.id} provider={provider} />
                  ))}
                </div>
              ))}
        </div>

        {/* Manage Providers Modal */}
        <ManageProvidersModal
          isOpen={isManageModalOpen}
          onClose={() => setIsManageModalOpen(false)}
        />

        {/* Add Provider Modal */}
        {matterId && (
          <AddProviderModal
            isOpen={isAddProviderOpen}
            onClose={() => setIsAddProviderOpen(false)}
            matterId={matterId}
            centerLat={incidentCenter?.lat}
            centerLng={incidentCenter?.lng}
            onProviderCreated={() => setIsAddProviderOpen(false)}
          />
        )}
      </div>
    </>
  );
};
